import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../layout/Layout';
import { useAuth } from '../../context/AuthContext';
import { myceliumApi, networkApi, organisationApi, setNetworkInstallationUuid, setNetworkOwnerOrgId } from '../../services/api';
import AdminPacketStats from './AdminPacketStats';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import {
  normalizeNetworkDevice as normalizeDevice,
  normalizeNetworkInterfaceAccountReport as normalizeInterfaceAccountReport,
  normalizeManagedNetworkInterface,
  getNetworkDeviceDisplayStatus as getDeviceDisplayStatus,
} from './AdminNetwork.utils';
import './AdminNetwork.css';

const extractData = (response) => response?.data?.data ?? response?.data ?? null;

const useNetworkStepUp = () => {
  const [challenge, setChallenge] = useState(null);
  const [retryAction, setRetryAction] = useState(null);

  const handleStepUpError = useCallback((error, retry) => {
    const nextChallenge = getStepUpChallengeFromError(error);
    if (!nextChallenge) {
      return false;
    }

    setChallenge(nextChallenge);
    setRetryAction(() => retry);
    return true;
  }, []);

  const closeStepUp = useCallback(() => {
    setChallenge(null);
    setRetryAction(null);
  }, []);

  const completeStepUp = useCallback(async () => {
    const retry = retryAction;
    closeStepUp();
    if (retry) {
      await retry();
    }
  }, [closeStepUp, retryAction]);

  const stepUpDialog = (
    <StepUpDialog
      challenge={challenge}
      title="Network access needs 2FA"
      description="Network monitoring requires a fresh SAL 4 verification before protected data can load."
      onComplete={completeStepUp}
      onCancel={closeStepUp}
    />
  );

  return { handleStepUpError, stepUpDialog };
};

const pickNumber = (...values) => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
};

const pickString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return '';
};

const asArray = (value, keys = []) => {
  if (Array.isArray(value)) {
    return value;
  }

  for (const key of keys) {
    const candidate = value?.[key];
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

const getNetworkGatewayErrorCode = (error) => {
  const body = error?.response?.data || {};
  const structured = body.error && typeof body.error === 'object' ? body.error : null;
  return structured?.code || body.errorCode || body.code || (typeof body.error === 'string' ? body.error : '');
};

const getNetworkErrorMessage = (error, fallback) => {
  const code = getNetworkGatewayErrorCode(error);
  if (code === 'SAL_CEILING_EXCEEDED' || code === 'STEP_UP_REQUIRED') {
    return 'Complete network access 2FA before loading Mycelium network data.';
  }
  if (code === 'MYCELIUM_LINK_HUMAN_SESSION_REQUIRED') {
    return 'Open this page with a signed-in admin session to read the Mycelium link context.';
  }
  if (code === 'MYCELIUM_NETWORK_LINK_OFFLINE') {
    return 'No linked Mycelium network service is online for this organisation.';
  }
  if (code === 'MYCELIUM_NETWORK_ORG_REQUIRED') {
    return 'Select an organisation before loading the Mycelium network gateway.';
  }
  if (code === 'MYCELIUM_NETWORK_INSTALL_REQUIRED') {
    return 'Select a Mycelium source before loading this organisation network gateway.';
  }
  if (code === 'MYCELIUM_NETWORK_INSTALL_FORBIDDEN') {
    return 'This session cannot access the selected Mycelium source.';
  }
  if (code === 'MYCELIUM_NETWORK_ORG_FORBIDDEN') {
    return 'This session cannot access the selected organisation network gateway.';
  }
  if (code === 'MYCELIUM_NETWORK_STREAM_UNSUPPORTED') {
    return 'Live streaming is not available through the current Mycelium network gateway.';
  }
  if (code === 'MYCELIUM_NETWORK_UPSTREAM_ERROR') {
    return 'The linked Mycelium network service returned an upstream error.';
  }

  return error?.response?.data?.message || fallback;
};

const getStoredNetworkOwnerOrgId = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.sessionStorage.getItem('studioaeye_network_owner_org_id') || '';
};

const getStoredNetworkInstallationUuid = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.sessionStorage.getItem('studioaeye_network_installation_uuid') || '';
};

const normalizeNetworkOrganisation = (org) => {
  const id = org?.id ?? org?.organisation_id ?? org?.organisationId ?? org?.ownerOrgId ?? org?.owner_org_id;
  if (!id) {
    return null;
  }

  return {
    id: String(id),
    name: pickString(org?.name, org?.organisation_name, org?.displayName, org?.slug, `Organisation ${id}`),
  };
};

const getInstallationOwnerOrgId = (installation) => {
  const id = installation?.ownerOrgId
    ?? installation?.owner_org_id
    ?? installation?.organisationId
    ?? installation?.organisation_id
    ?? installation?.ownerOrganisation?.id
    ?? installation?.owner_organisation?.id;
  return id ? String(id) : '';
};

const isActiveMyceliumNetworkInstallation = (installation) => {
  const ownerOrgId = getInstallationOwnerOrgId(installation);
  const appCode = String(installation?.appCode || installation?.app_code || 'mycelium').toLowerCase();
  const status = String(installation?.status || installation?.state || '').toLowerCase();
  return Boolean(ownerOrgId) && appCode === 'mycelium' && status === 'active';
};

const sanitizeGatewayAddress = (value) => {
  const rawValue = pickString(value);
  if (!rawValue) {
    return '';
  }

  try {
    const parsed = new URL(rawValue);
    parsed.username = '';
    parsed.password = '';
    parsed.pathname = '';
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString().replace(/\/$/, '');
  } catch (_err) {
    return rawValue;
  }
};

const normalizeMyceliumGatewaySource = (installation) => {
  if (!installation) {
    return null;
  }

  const ownerOrgId = getInstallationOwnerOrgId(installation);
  if (!ownerOrgId) {
    return null;
  }

  return {
    ownerOrgId,
    uuid: pickString(
      installation?.uuid,
      installation?.installationUuid,
      installation?.installation_uuid,
      installation?.myceliumInstallationUuid,
      installation?.mycelium_installation_uuid,
    ),
    kind: 'Mycelium gateway',
    label: pickString(
      installation?.installLabel,
      installation?.install_label,
      installation?.nodeLabel,
      installation?.node_label,
      installation?.hostname,
      installation?.host,
      installation?.name,
    ),
    hostname: pickString(
      installation?.hostname,
      installation?.host,
      installation?.nodeHostname,
      installation?.node_hostname,
    ),
    address: sanitizeGatewayAddress(
      installation?.localBaseUrl
      || installation?.local_base_url
      || installation?.lanAddress
      || installation?.lan_address
      || installation?.lanHost
      || installation?.lan_host
      || installation?.lastSeenIp
      || installation?.last_seen_ip
      || installation?.ipAddress
      || installation?.ip_address,
    ),
    state: pickString(
      installation?.connectionState,
      installation?.connection_state,
      installation?.linkState,
      installation?.link_state,
      installation?.status,
      installation?.state,
      'unknown',
    ),
    lastSeenAt: pickString(
      installation?.lastSeenAt,
      installation?.last_seen_at,
      installation?.lastSeen,
      installation?.last_seen,
    ),
  };
};

const mergeNetworkOrganisations = (...groups) => {
  const byId = new Map();

  groups.flat().forEach((org) => {
    if (!org?.id || byId.has(org.id)) {
      return;
    }
    byId.set(org.id, org);
  });

  return Array.from(byId.values());
};

const getGatewaySourceKey = (source) => source?.uuid || `${source?.ownerOrgId || 'owner'}-${source?.label || source?.address || source?.hostname || 'source'}`;

const getGatewaySourceDisplayLabel = (source) => {
  if (!source) {
    return 'Mycelium gateway';
  }

  const parts = [
    source.label || 'Mycelium gateway',
    source.hostname,
    source.address,
  ].filter(Boolean);

  return parts.filter((part, index) => parts.indexOf(part) === index).join(' · ');
};

/**
 * Resolve installation UUID for a set of sources.
 * Prefer explicit preferred UUID; only auto-select when there is exactly one node
 * (fail-closed for multi-node — never guess).
 */
const pickGatewaySourceUuid = (sources, preferredUuid = '') => {
  if (!Array.isArray(sources) || sources.length === 0) {
    return '';
  }

  if (preferredUuid && sources.some((source) => source.uuid === preferredUuid)) {
    return preferredUuid;
  }

  if (sources.length === 1) {
    return sources[0]?.uuid || '';
  }

  return '';
};

/** M004-F0314 / T-M004-0001 — build Network paths that preserve ownerOrgId + installationUuid. */
export const buildNetworkPath = (pathname, scopeParams = {}, extraParams = {}) => {
  const path = pathname || '/app/admin/network';
  const params = new URLSearchParams();
  Object.entries(extraParams || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== '') {
      params.set(key, String(value));
    }
  });
  if (scopeParams?.ownerOrgId) {
    params.set('ownerOrgId', String(scopeParams.ownerOrgId));
  }
  if (scopeParams?.installationUuid) {
    params.set('installationUuid', String(scopeParams.installationUuid));
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

const normalizeCatalogueNode = (node) => {
  if (!node) {
    return null;
  }
  return normalizeMyceliumGatewaySource({
    uuid: node.installationUuid || node.uuid,
    installationUuid: node.installationUuid || node.uuid,
    installLabel: node.installLabel || node.label,
    hostname: node.hostname,
    localBaseUrl: node.localBaseUrl || node.reportedAddress,
    lastSeenIp: node.lastSeenIp,
    ownerOrgId: node.ownerOrgId,
    connectionState: node.connectionState,
    status: node.status || 'active',
    lastSeenAt: node.lastSeenAt,
    ownerOrganisation: node.ownerOrganisation,
  });
};

const getGatewayAddressHref = (address) => {
  if (!address) {
    return '';
  }
  try {
    const parsed = new URL(address);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.toString() : '';
  } catch (_err) {
    return '';
  }
};

const useNetworkOwnerOrgScope = (handleStepUpError) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlOwnerOrgId = searchParams.get('ownerOrgId') || '';
  const urlInstallationUuid = searchParams.get('installationUuid') || '';

  const [organisations, setOrganisations] = useState([]);
  const [ownerOrgId, setOwnerOrgIdState] = useState(urlOwnerOrgId || getStoredNetworkOwnerOrgId());
  const [selectedInstallationUuid, setSelectedInstallationUuidState] = useState(
    urlInstallationUuid || getStoredNetworkInstallationUuid(),
  );
  const [gatewaySourcesByOwner, setGatewaySourcesByOwner] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Bumped when the operator switches node so child views can clear node-local state.
  const [scopeGeneration, setScopeGeneration] = useState(0);
  const mountedRef = useRef(false);

  const writeScopeToUrl = useCallback((nextOwnerOrgId, nextInstallationUuid) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextOwnerOrgId) {
        next.set('ownerOrgId', String(nextOwnerOrgId));
      } else {
        next.delete('ownerOrgId');
      }
      if (nextInstallationUuid) {
        next.set('installationUuid', String(nextInstallationUuid));
      } else {
        next.delete('installationUuid');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    mountedRef.current = true;

    const loadCatalogue = async () => {
      try {
        setLoading(true);
        setError('');
        const [organisationsResult, nodesResult] = await Promise.allSettled([
          organisationApi.getMine(),
          myceliumApi.listNetworkNodes(),
        ]);
        if (
          nodesResult.status === 'rejected'
          && handleStepUpError?.(nodesResult.reason, loadCatalogue)
        ) {
          if (mountedRef.current) {
            setError('');
          }
          return;
        }
        const organisationsPayload = organisationsResult.status === 'fulfilled' ? extractData(organisationsResult.value) : null;
        const nodesPayload = nodesResult.status === 'fulfilled' ? extractData(nodesResult.value) : null;
        const listedOrganisations = asArray(organisationsPayload, ['organisations', 'items', 'rows'])
          .map(normalizeNetworkOrganisation)
          .filter(Boolean);
        const catalogueNodes = asArray(nodesPayload, ['nodes', 'installations'])
          .map(normalizeCatalogueNode)
          .filter(Boolean);

        const nextGatewaySourcesByOwner = catalogueNodes.reduce((sources, source) => {
          const ownerSources = sources[source.ownerOrgId] || [];
          if (!ownerSources.some((entry) => getGatewaySourceKey(entry) === getGatewaySourceKey(source))) {
            sources[source.ownerOrgId] = [...ownerSources, source];
          }
          return sources;
        }, {});

        const orgFromNodes = catalogueNodes
          .map((node) => normalizeNetworkOrganisation({
            id: node.ownerOrgId,
            name: node.ownerOrganisation?.name,
          }))
          .filter(Boolean);
        const nextOrganisations = mergeNetworkOrganisations(listedOrganisations, orgFromNodes);
        const activeLinkedOwnerOrgIds = Object.keys(nextGatewaySourcesByOwner);

        if (!mountedRef.current) {
          return;
        }

        setOrganisations(nextOrganisations);
        setGatewaySourcesByOwner(nextGatewaySourcesByOwner);

        // URL is source of truth; session storage only restores when URL is empty.
        const preferredOwner = urlOwnerOrgId || getStoredNetworkOwnerOrgId() || '';
        const preferredInstall = urlInstallationUuid || getStoredNetworkInstallationUuid() || '';
        const linkedOwnerOrgId = activeLinkedOwnerOrgIds.includes(preferredOwner)
          ? preferredOwner
          : (activeLinkedOwnerOrgIds.length === 1 ? activeLinkedOwnerOrgIds[0] : preferredOwner);
        const currentStillAvailable = nextOrganisations.some((org) => org.id === linkedOwnerOrgId);
        const nextOwnerOrgId = (currentStillAvailable && linkedOwnerOrgId)
          || (activeLinkedOwnerOrgIds[0] || '')
          || (nextOrganisations[0]?.id || '');
        const nextOwnerSources = nextGatewaySourcesByOwner[nextOwnerOrgId] || [];
        const nextInstallationUuid = pickGatewaySourceUuid(nextOwnerSources, preferredInstall);

        setOwnerOrgIdState(nextOwnerOrgId);
        setNetworkOwnerOrgId(nextOwnerOrgId);
        setSelectedInstallationUuidState(nextInstallationUuid);
        setNetworkInstallationUuid(nextInstallationUuid);
        writeScopeToUrl(nextOwnerOrgId, nextInstallationUuid);

        if (nextOrganisations.length === 0 && organisationsResult.status === 'rejected' && nodesResult.status === 'rejected') {
          setError(getNetworkErrorMessage(
            organisationsResult.reason || nodesResult.reason,
            'Unable to load organisations for the network gateway.',
          ));
        } else if (nodesResult.status === 'rejected') {
          setError(getNetworkErrorMessage(nodesResult.reason, 'Unable to load Mycelium node catalogue for this session.'));
        }
      } catch (err) {
        if (mountedRef.current) {
          setError(getNetworkErrorMessage(err, 'Unable to load organisations for the network gateway.'));
          setOrganisations([]);
          setGatewaySourcesByOwner({});
          setOwnerOrgIdState('');
          setNetworkOwnerOrgId('');
          setSelectedInstallationUuidState('');
          setNetworkInstallationUuid('');
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadCatalogue();
    return () => {
      mountedRef.current = false;
    };
    // Intentionally mount-only: catalogue load + initial URL write; URL sync is handled below.
  }, []);

  // Keep local state aligned when URL changes (back/forward, shared links).
  useEffect(() => {
    if (urlOwnerOrgId && urlOwnerOrgId !== ownerOrgId) {
      setOwnerOrgIdState(urlOwnerOrgId);
      setNetworkOwnerOrgId(urlOwnerOrgId);
      setScopeGeneration((value) => value + 1);
    }
    if (urlInstallationUuid && urlInstallationUuid !== selectedInstallationUuid) {
      setSelectedInstallationUuidState(urlInstallationUuid);
      setNetworkInstallationUuid(urlInstallationUuid);
      setScopeGeneration((value) => value + 1);
    }
  }, [urlOwnerOrgId, urlInstallationUuid, ownerOrgId, selectedInstallationUuid]);

  const selectOwnerOrgId = useCallback((nextOwnerOrgId) => {
    const normalized = nextOwnerOrgId ? String(nextOwnerOrgId) : '';
    const nextInstallationUuid = pickGatewaySourceUuid(
      gatewaySourcesByOwner[normalized] || [],
      selectedInstallationUuid,
    );
    setOwnerOrgIdState(normalized);
    setNetworkOwnerOrgId(normalized);
    setSelectedInstallationUuidState(nextInstallationUuid);
    setNetworkInstallationUuid(nextInstallationUuid);
    writeScopeToUrl(normalized, nextInstallationUuid);
    setScopeGeneration((value) => value + 1);
  }, [gatewaySourcesByOwner, selectedInstallationUuid, writeScopeToUrl]);

  const selectInstallationUuid = useCallback((nextInstallationUuid) => {
    const normalized = nextInstallationUuid ? String(nextInstallationUuid) : '';
    setSelectedInstallationUuidState(normalized);
    setNetworkInstallationUuid(normalized);
    writeScopeToUrl(ownerOrgId, normalized);
    setScopeGeneration((value) => value + 1);
  }, [ownerOrgId, writeScopeToUrl]);

  const gatewaySources = gatewaySourcesByOwner[ownerOrgId] || [];
  // Never fall back to another node's source when multi-node and nothing selected.
  const gatewaySource = gatewaySources.find((source) => source.uuid && source.uuid === selectedInstallationUuid)
    || (gatewaySources.length === 1 ? gatewaySources[0] : null)
    || null;
  const scopedInstallationUuid = gatewaySource?.uuid || selectedInstallationUuid || '';

  return {
    organisations,
    ownerOrgId,
    selectedInstallationUuid: scopedInstallationUuid,
    loading,
    error,
    gatewaySources,
    gatewaySource,
    scopeGeneration,
    selectedOrganisation: organisations.find((org) => org.id === ownerOrgId) || null,
    scopeParams: ownerOrgId && scopedInstallationUuid ? {
      ownerOrgId,
      installationUuid: scopedInstallationUuid,
    } : (ownerOrgId ? { ownerOrgId } : {}),
    selectOwnerOrgId,
    selectInstallationUuid,
    buildPath: (pathname, extraParams) => buildNetworkPath(
      pathname,
      ownerOrgId && scopedInstallationUuid
        ? { ownerOrgId, installationUuid: scopedInstallationUuid }
        : (ownerOrgId ? { ownerOrgId } : {}),
      extraParams,
    ),
  };
};

const NetworkGatewaySourceSummary = ({ gatewaySource }) => {
  const addressHref = getGatewayAddressHref(gatewaySource?.address);

  return (
    <div className="network-scope-details" data-testid="WEB-MYC-NET-GW-002_source">
    <div>
      <span>Source</span>
      <strong>{gatewaySource?.kind || 'Mycelium gateway'}</strong>
    </div>
    <div>
      <span>Node</span>
      <strong>{gatewaySource?.label || 'Not reported'}</strong>
    </div>
    {gatewaySource?.hostname ? (
      <div>
        <span>Host</span>
        <strong>{gatewaySource.hostname}</strong>
      </div>
    ) : null}
      <div>
        <span>LAN/local address</span>
        <strong>
          {addressHref ? (
            <a
              href={addressHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="WEB-MYC-NET-NODE-001_address_link"
            >
              {gatewaySource.address}
            </a>
          ) : (gatewaySource?.address || 'Not reported')}
        </strong>
      </div>
    <div>
      <span>State</span>
      <strong>{formatLabel(gatewaySource?.state || 'unknown')}</strong>
    </div>
    {gatewaySource?.lastSeenAt ? (
      <div>
        <span>Last seen</span>
        <strong>{formatDateTime(gatewaySource.lastSeenAt)}</strong>
      </div>
    ) : null}
    <p className="network-scope-isolation" data-testid="WEB-MYC-NET-NODE-001_isolation">
      All visible network results are from the selected Mycelium node only. Node inventories are never merged.
    </p>
    </div>
  );
};

const NetworkGatewaySourceControl = ({ gatewaySources, selectedInstallationUuid, gatewaySource, onInstallationUuidChange }) => {
  if (!gatewaySources || gatewaySources.length === 0) {
    return (
      <div className="network-scope-install-control" data-testid="WEB-MYC-NET-NODE-001_empty">
        <span>No active Mycelium node is linked for this organisation.</span>
      </div>
    );
  }

  if (gatewaySources.length === 1) {
    return <NetworkGatewaySourceSummary gatewaySource={gatewaySource || gatewaySources[0]} />;
  }

  return (
    <>
      <div className="network-scope-install-control">
        <label htmlFor="network-mycelium-install">Mycelium node</label>
        <select
          id="network-mycelium-install"
          className="network-select"
          value={selectedInstallationUuid || ''}
          onChange={(event) => onInstallationUuidChange(event.target.value)}
          data-testid="WEB-MYC-NET-GW-003_selector"
        >
          <option value="">Select a Mycelium node…</option>
          {gatewaySources.map((source) => (
            <option key={getGatewaySourceKey(source)} value={source.uuid || ''}>
              {getGatewaySourceDisplayLabel(source)}
            </option>
          ))}
        </select>
      </div>
      {gatewaySource ? (
        <NetworkGatewaySourceSummary gatewaySource={gatewaySource} />
      ) : (
        <p className="network-scope-isolation" data-testid="WEB-MYC-NET-NODE-001_need_select">
          Select a Mycelium node before loading network data. Multi-node organisations never use a default.
        </p>
      )}
    </>
  );
};

const NetworkOwnerOrgPanel = ({
  organisations,
  ownerOrgId,
  loading,
  error,
  gatewaySources,
  selectedInstallationUuid,
  gatewaySource,
  onOwnerOrgIdChange,
  onInstallationUuidChange,
}) => {
  if (loading) {
    return (
      <section className="network-scope-panel" aria-live="polite">
        <span>Loading network organisation...</span>
      </section>
    );
  }

  if (error) {
    return <div className="network-error-banner">{error}</div>;
  }

  if (organisations.length === 0) {
    return (
      <section className="network-scope-panel">
        <strong>No network organisation available</strong>
        <span>Link Mycelium to an organisation before using the network monitor.</span>
      </section>
    );
  }

  if (organisations.length === 1) {
    return (
      <section className="network-scope-panel">
        <div>
          <span>Organisation</span>
          <strong>{organisations[0].name}</strong>
        </div>
        <NetworkGatewaySourceControl
          gatewaySources={gatewaySources}
          selectedInstallationUuid={selectedInstallationUuid}
          gatewaySource={gatewaySource}
          onInstallationUuidChange={onInstallationUuidChange}
        />
      </section>
    );
  }

  return (
    <section className="network-scope-panel">
      <div>
        <label htmlFor="network-owner-org">Organisation</label>
        <select
          id="network-owner-org"
          className="network-select"
          value={ownerOrgId}
          onChange={(event) => onOwnerOrgIdChange(event.target.value)}
        >
          {organisations.map((org) => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      </div>
      <NetworkGatewaySourceControl
        gatewaySources={gatewaySources}
        selectedInstallationUuid={selectedInstallationUuid}
        gatewaySource={gatewaySource}
        onInstallationUuidChange={onInstallationUuidChange}
      />
    </section>
  );
};

const WEB_INTERFACE_HTTP_PORTS = new Set([80, 81, 8080, 8081, 8123, 9000, 9090]);
const WEB_INTERFACE_HTTPS_PORTS = new Set([443, 5001, 7443, 8443, 9443]);

const severityRank = {
  CRITICAL: 4,
  HIGH: 3,
  WARNING: 2,
  INFO: 1,
  healthy: 1,
  degraded: 2,
  warning: 2,
  offline: 4,
  critical: 4,
};

const getSeverityClass = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'critical' || normalized === 'offline' || normalized === 'error') {
    return 'critical';
  }
  if (normalized === 'high' || normalized === 'degraded') {
    return 'high';
  }
  if (normalized === 'warning') {
    return 'warning';
  }
  return 'info';
};

const formatDateTime = (value) => {
  if (!value) {
    return 'Unknown';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (value) => {
  if (!Number.isFinite(value)) {
    return 'n/a';
  }
  if (value < 1000) {
    return `${value} ms`;
  }
  return `${(value / 1000).toFixed(1)} s`;
};

const formatLatency = (value) => {
  if (!Number.isFinite(value)) {
    return 'n/a';
  }
  return `${value.toFixed(1)} ms`;
};

const compareText = (left, right) => String(left || '').localeCompare(String(right || ''), undefined, { numeric: true, sensitivity: 'base' });

const compareIpAddress = (left, right) => {
  const leftParts = String(left || '').split('.').map((part) => Number(part));
  const rightParts = String(right || '').split('.').map((part) => Number(part));
  const maxLength = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const leftValue = Number.isFinite(leftParts[index]) ? leftParts[index] : -1;
    const rightValue = Number.isFinite(rightParts[index]) ? rightParts[index] : -1;
    if (leftValue !== rightValue) {
      return leftValue - rightValue;
    }
  }

  return 0;
};

const formatBoolean = (value) => (value ? 'Yes' : 'No');

const formatLabel = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/^./, (char) => char.toUpperCase());

const formatMetadataValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Unknown';
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.map((entry) => formatMetadataValue(entry)).join(', ') : 'None';
  }

  if (typeof value === 'boolean') {
    return formatBoolean(value);
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
};

const createManagedInterfaceDraft = (overrides = {}) => ({
  id: null,
  type: 'onvif',
  label: '',
  baseUrl: '',
  deviceId: '',
  username: '',
  password: '',
  apiKey: '',
  tlsVerify: false,
  enabled: true,
  notes: '',
  ...overrides,
});

const getSuggestedManagedInterfaceType = (device) => {
  const deviceType = String(device?.deviceType || '').toLowerCase();
  const manufacturer = String(device?.manufacturer || '').toLowerCase();
  const hostname = String(device?.hostname || '').toLowerCase();

  if (deviceType.includes('sonoff') || deviceType.includes('sonoff_stock') || hostname.includes('ewelink') || manufacturer.includes('ewelink')) {
    return 'sonoff_diy';
  }
  if (deviceType.includes('truenas') || deviceType.includes('nas')) {
    return 'truenas_rest';
  }
  if (deviceType.includes('pfsense') || deviceType.includes('firewall') || deviceType.includes('router')) {
    return 'pfsense';
  }
  if (deviceType.includes('nvr') || deviceType.includes('recorder')) {
    return 'isapi';
  }
  if (deviceType.includes('camera') || deviceType.includes('onvif')) {
    return 'onvif';
  }

  return 'onvif';
};

const getSuggestedManagedInterfaceBaseUrl = (device, type) => {
  const ipAddress = pickString(device?.ipAddress);

  if (!ipAddress) {
    return '';
  }

  switch (type) {
    case 'truenas_rest':
    case 'truenas_websocket':
      return `https://${ipAddress}/api/v2.0`;
    case 'onvif':
      return `http://${ipAddress}/onvif/device_service`;
    case 'pfsense':
      return `https://${ipAddress}`;
    case 'sonoff_diy':
    case 'sonoff_lan': {
      const sonoffPort = device?.details?.port || 8081;
      return `http://${ipAddress}:${sonoffPort}`;
    }
    case 'isapi':
    default:
      return `https://${ipAddress}`;
  }
};

const getSuggestedManagedInterfaceDraft = (device, overrides = {}) => {
  const type = overrides.type || getSuggestedManagedInterfaceType(device);
  const isSonoff = type === 'sonoff_diy' || type === 'sonoff_lan';

  return createManagedInterfaceDraft({
    type,
    label: overrides.label || '',
    baseUrl: overrides.baseUrl || getSuggestedManagedInterfaceBaseUrl(device, type),
    ...(isSonoff ? { deviceId: device?.details?.deviceId || device?.details?.id || device?.details?.passive?.mdns?.txtRecords?.id || device?.details?.passive?.mdns?.txt?.id || '' } : {}),
    ...overrides,
  });
};

const getOnboardingState = (device, managedInterfaces) => {
  const explicitState = pickString(device?.onboardingState);
  if (explicitState) {
    return explicitState;
  }

  if (!device?.approved) {
    return 'discovered';
  }

  if (managedInterfaces.length === 0) {
    return device?.stableAddressRequired && !device?.addressingMode ? 'addressing_review' : 'approved';
  }

  const displayStatus = getDeviceDisplayStatus(device);
  if (displayStatus === 'degraded' || displayStatus === 'warning' || displayStatus === 'offline') {
    return 'degraded';
  }

  return 'verified';
};

const getOnboardingGuidance = (device, managedInterfaces) => {
  if (!device?.approved) {
    return 'Review the device, confirm it is legitimate, and save approval before finishing onboarding.';
  }

  if (device?.stableAddressRequired && !device?.addressingMode) {
    return 'Addressing still needs review. The backend has not exposed the write endpoint yet, so record the recommendation here before completing the interface setup.';
  }

  if (managedInterfaces.length === 0) {
    return 'Add at least one managed interface to upgrade this device from reachability-only monitoring to authenticated health checks.';
  }

  if (getDeviceDisplayStatus(device) === 'degraded') {
    return 'The device is onboarded but still degraded. Re-test the managed interface and review any integrity findings before calling it complete.';
  }

  return 'This device has enough onboarding data to be treated as managed. Review addressing and interface health periodically.';
};

const toMetadataEntries = (value, prefix = '') => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }

  return Object.entries(value).flatMap(([key, entryValue]) => {
    const nextKey = prefix ? `${prefix} / ${formatLabel(key)}` : formatLabel(key);

    if (entryValue && typeof entryValue === 'object' && !Array.isArray(entryValue)) {
      const nestedEntries = toMetadataEntries(entryValue, nextKey);
      return nestedEntries.length > 0 ? nestedEntries : [{ key: nextKey, value: formatMetadataValue(entryValue) }];
    }

    return [{ key: nextKey, value: formatMetadataValue(entryValue) }];
  });
};

const mapSystem = (key, raw) => {
  const entry = raw && typeof raw === 'object' ? raw : {};
  return {
    key,
    name: pickString(entry.name, entry.label, key.replace(/_/g, ' ')) || 'System',
    status: pickString(entry.status, entry.health, entry.state, 'unknown'),
    detail: pickString(entry.detail, entry.message, entry.summary),
    issueCount: pickNumber(entry.issueCount, entry.issue_count, entry.alertCount, entry.alert_count),
    updatedAt: pickString(entry.updatedAt, entry.updated_at, entry.checkedAt, entry.checked_at),
  };
};

const normalizeSummary = (payload) => {
  const data = payload && typeof payload === 'object' ? payload : {};
  const systemsInput = data.systems || data.health || data.subsystems || data.tiles || {};
  let systems = [];

  if (Array.isArray(systemsInput)) {
    systems = systemsInput.map((entry, index) => mapSystem(entry.key || entry.name || `system-${index}`, entry));
  } else if (systemsInput && typeof systemsInput === 'object') {
    systems = Object.entries(systemsInput).map(([key, entry]) => mapSystem(key, entry));
  }

  const unresolvedAnomalies = pickNumber(
    data.unresolvedAnomalies,
    data.unresolved_anomalies,
    data.openAnomalies,
    data.open_anomalies,
    data.anomalyCount,
    data.anomaly_count
  );

  const deviceCount = pickNumber(
    data.deviceCount,
    data.device_count,
    data.totalDevices,
    data.total_devices,
    data.devices?.total,
    data.devicesTotal
  );

  const criticalDevices = pickNumber(
    data.criticalDevices,
    data.critical_devices,
    data.devices?.critical,
    data.criticalCount
  );

  const onlineDevices = pickNumber(
    data.onlineDevices,
    data.online_devices,
    data.devices?.online,
    data.onlineCount
  );

  const recentAnomalies = asArray(data, ['recentAnomalies', 'recent_anomalies', 'anomalies']).map(normalizeAnomaly);
  const recentScans = asArray(data, ['recentScans', 'recent_scans', 'scans']).map(normalizeScan);

  const derivedStatus = systems.reduce((current, system) => {
    const nextRank = severityRank[String(system.status || '').toLowerCase()] || 0;
    return nextRank > (severityRank[current] || 0) ? String(system.status || '').toLowerCase() : current;
  }, unresolvedAnomalies > 0 ? 'warning' : 'healthy');

  return {
    status: pickString(data.status, data.healthStatus, data.health_status, derivedStatus) || 'unknown',
    unresolvedAnomalies,
    deviceCount,
    criticalDevices,
    onlineDevices,
    systems,
    recentAnomalies,
    recentScans,
    lastScanAt: pickString(data.lastScanAt, data.last_scan_at, data.updatedAt, data.updated_at),
  };
};

const normalizeWarningEntry = (warning) => {
  const data = warning && typeof warning === 'object' ? warning : {};

  return {
    id: data.id,
    title: pickString(data.title, data.warningTitle, data.warning_title, data.code, 'Warning'),
    code: pickString(data.code, data.warningCode, data.warning_code),
    severity: pickString(data.severity, data.level, 'warning').toUpperCase(),
    meaning: pickString(data.meaning, data.description, data.detail, data.message),
    optionASummary: pickString(data.optionASummary, data.option_a_summary, data.optionA, data.option_a),
    state: pickString(data.state, data.status, data.lifecycleState, data.lifecycle_state, data.resolved ? 'resolved' : 'open') || 'open',
    blocking: Boolean(data.blocking ?? data.isBlocking ?? data.is_blocking ?? false),
    acknowledged: Boolean(data.acknowledged ?? data.isAcknowledged ?? data.is_acknowledged ?? false),
    resolved: Boolean(data.resolved ?? data.isResolved ?? data.is_resolved ?? false),
  };
};

const normalizeDependencyEntry = (entry) => {
  const data = entry && typeof entry === 'object' ? entry : {};

  return {
    id: data.id,
    label: pickString(data.label, data.name, data.title, data.type, 'Dependency'),
    configured: pickString(data.configured, data.configuredValue, data.configured_value, data.expected),
    observed: pickString(data.observed, data.observedValue, data.observed_value, data.actual),
    inferred: pickString(data.inferred, data.inferredValue, data.inferred_value),
    unknown: pickString(data.unknown, data.unknownValue, data.unknown_value),
    status: pickString(data.status, data.health, data.state, 'info'),
  };
};

const normalizeHistoryEntry = (entry) => {
  const data = entry && typeof entry === 'object' ? entry : {};

  return {
    id: data.id,
    eventType: pickString(data.eventType, data.event_type, data.type, 'event'),
    summary: pickString(data.summary, data.message, data.detail, data.description),
    previousValue: pickString(data.previousValue, data.previous_value, data.oldValue, data.old_value),
    nextValue: pickString(data.nextValue, data.next_value, data.newValue, data.new_value),
    detectedAt: pickString(data.detectedAt, data.detected_at, data.createdAt, data.created_at, data.timestamp),
  };
};

const getManagedInterfaceVerificationBlocker = (device, managedInterfaces, warnings) => {
  if (!device?.approved) {
    return 'Blocked: device approval still required';
  }

  if (device?.stableAddressRequired && !device?.addressingMode) {
    return 'Blocked: address review still required';
  }

  if (managedInterfaces.length === 0) {
    return 'Blocked: no working managed interface configured';
  }

  const hasWorkingInterface = managedInterfaces.some((entry) => {
    const healthState = String(entry.healthState || '').toLowerCase();
    const credentialState = String(entry.credentialState || '').toLowerCase();
    const testResult = String(entry.lastTestResult || '').toLowerCase();

    return credentialState === 'configured' || testResult === 'success' || healthState === 'healthy' || healthState === 'degraded';
  });

  if (!hasWorkingInterface) {
    return 'Blocked: no working managed interface configured';
  }

  const blockingWarning = warnings.find((warning) => warning.blocking && !warning.resolved);
  if (blockingWarning) {
    return `Blocked: ${blockingWarning.title}`;
  }

  return '';
};

const getInterfaceIntegritySeverity = (report) => {
  if (!report) {
    return 'info';
  }
  if (report.integrityStatus === 'ok') {
    return 'healthy';
  }
  if (report.integrityStatus === 'expected_account_missing') {
    return 'warning';
  }
  return 'critical';
};

const getFingerprintWebInterfaceUrl = (device) => {
  const networkProbe = device?.details?.networkProbe ?? device?.details?.network_probe ?? {};
  const httpFingerprints = asArray(networkProbe, ['httpFingerprints', 'http_fingerprints']);

  for (const fingerprint of httpFingerprints) {
    const url = pickString(fingerprint?.url);
    if (url) {
      return url;
    }
  }

  return '';
};

const inferWebInterfaceScheme = (portInfo) => {
  const service = String(portInfo?.service || '').toLowerCase();
  const description = String(portInfo?.description || '').toLowerCase();
  const port = Number(portInfo?.port);

  if (service.includes('https') || description.includes('https') || WEB_INTERFACE_HTTPS_PORTS.has(port)) {
    return 'https';
  }

  return 'http';
};

const isWebInterfacePort = (portInfo) => {
  const service = String(portInfo?.service || '').toLowerCase();
  const description = String(portInfo?.description || '').toLowerCase();
  const port = Number(portInfo?.port);

  if (!Number.isFinite(port)) {
    return false;
  }

  return service.includes('http')
    || service.includes('home-assistant')
    || description.includes('admin/ui')
    || description.includes('web ui')
    || description.includes('http')
    || description.includes('https')
    || WEB_INTERFACE_HTTP_PORTS.has(port)
    || WEB_INTERFACE_HTTPS_PORTS.has(port);
};

const getDeviceWebInterfaceUrl = (device) => {
  if (!device?.ipAddress) {
    return '';
  }

  const fingerprintUrl = getFingerprintWebInterfaceUrl(device);
  if (fingerprintUrl) {
    return fingerprintUrl;
  }

  const sortedOpenPorts = [...asArray(device?.openPorts)].sort((left, right) => {
    const leftScheme = inferWebInterfaceScheme(left);
    const rightScheme = inferWebInterfaceScheme(right);

    if (leftScheme !== rightScheme) {
      return leftScheme === 'https' ? -1 : 1;
    }

    return Number(left?.port || 0) - Number(right?.port || 0);
  });

  const webPort = sortedOpenPorts.find(isWebInterfacePort);
  if (!webPort) {
    return '';
  }

  const scheme = inferWebInterfaceScheme(webPort);
  const port = Number(webPort.port);
  const includePort = !((scheme === 'http' && port === 80) || (scheme === 'https' && port === 443));

  return `${scheme}://${device.ipAddress}${includePort ? `:${port}` : ''}/`;
};

function normalizeAnomaly(anomaly) {
  return {
    id: anomaly?.id,
    type: pickString(anomaly?.type, anomaly?.anomaly_type, 'UNKNOWN'),
    severity: pickString(anomaly?.severity, 'INFO').toUpperCase(),
    title: pickString(anomaly?.title, anomaly?.anomaly_type, 'Network anomaly'),
    detail: pickString(anomaly?.detail, anomaly?.message, anomaly?.description),
    sourceIp: pickString(anomaly?.sourceIp, anomaly?.source_ip),
    sourceMac: pickString(anomaly?.sourceMac, anomaly?.source_mac),
    detectedAt: pickString(anomaly?.detectedAt, anomaly?.detected_at, anomaly?.createdAt, anomaly?.created_at),
    resolvedAt: pickString(anomaly?.resolvedAt, anomaly?.resolved_at),
    resolved: Boolean(anomaly?.resolvedAt ?? anomaly?.resolved_at ?? anomaly?.resolved),
  };
}

function normalizeScan(scan) {
  return {
    id: scan?.id,
    type: pickString(scan?.scanType, scan?.scan_type, 'unknown'),
    startedAt: pickString(scan?.startedAt, scan?.started_at),
    completedAt: pickString(scan?.completedAt, scan?.completed_at),
    devicesFound: pickNumber(scan?.devicesFound, scan?.devices_found),
    newDevices: pickNumber(scan?.newDevices, scan?.new_devices),
    anomaliesFound: pickNumber(scan?.anomaliesFound, scan?.anomalies_found),
    durationMs: pickNumber(scan?.durationMs, scan?.duration_ms),
    status: pickString(scan?.status, 'unknown'),
    errorMessage: pickString(scan?.errorMessage, scan?.error_message),
  };
}

const EmptyState = ({ title, text }) => (
  <div className="network-empty-state">
    <strong>{title}</strong>
    <p>{text}</p>
  </div>
);

const LoadingPanel = ({ title = 'Loading network monitor...' }) => (
  <div className="network-loading-panel">{title}</div>
);

const SectionHeader = ({ title, subtitle, actions }) => (
  <div className="network-section-header">
    <div>
      <h1>{title}</h1>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
    {actions ? <div className="network-header-actions">{actions}</div> : null}
  </div>
);

const AdminNetworkPlaceholderView = ({ title, subtitle, message, primaryLink, secondaryLink }) => (
  <Layout>
    <div className="network-page">
      <SectionHeader title={title} subtitle={subtitle} />
      <section className="network-panel">
        <EmptyState title={title} text={message} />
        <div className="network-header-actions">
          {primaryLink ? <Link to={primaryLink.to} className="network-primary-link">{primaryLink.label}</Link> : null}
          {secondaryLink ? <Link to={secondaryLink.to} className="network-secondary-link">{secondaryLink.label}</Link> : null}
        </div>
      </section>
    </div>
  </Layout>
);

const SummaryCard = ({ label, value, tone = 'info', helper }) => (
  <div className={`network-summary-card tone-${tone}`}>
    <span className="network-summary-label">{label}</span>
    <strong className="network-summary-value">{value}</strong>
    {helper ? <span className="network-summary-helper">{helper}</span> : null}
  </div>
);

const TRIGGER_SCAN_DESCRIPTIONS = {
  all: 'Run the full network monitoring bundle and refresh the network summary.',
  discovery: 'Start an immediate subnet discovery sweep. Optionally force the scanner tool.',
  truenas: 'Refresh the TrueNAS health checks only.',
  ha: 'Refresh the Home Assistant health checks only.',
  summary: 'Refresh the aggregate health summary without running a full discovery.',
};

const normalizeTriggerResult = (payload) => {
  const data = payload && typeof payload === 'object' ? payload : {};
  const result = data.result && typeof data.result === 'object' ? data.result : {};

  return {
    message: pickString(data.message, 'Scan triggered'),
    scanType: pickString(data.scanType, data.scan_type),
    tool: pickString(data.tool, result.tool),
    requestedTool: pickString(data.requestedTool, data.requested_tool, result.requestedTool, result.requested_tool),
    triggeredAt: pickString(data.triggeredAt, data.triggered_at),
    status: pickString(result.status, data.status),
    subnet: pickString(result.subnet, data.subnet),
    devicesFound: pickNumber(result.devicesFound, result.devices_found, data.devicesFound, data.devices_found),
    newDevices: pickNumber(result.newDevices, result.new_devices, data.newDevices, data.new_devices),
    anomaliesFound: pickNumber(result.anomaliesFound, result.anomalies_found, data.anomaliesFound, data.anomalies_found),
  };
};

const TRIGGER_SCAN_TYPE_OPTIONS = [
  { value: 'all', label: 'Run all checks' },
  { value: 'discovery', label: 'Discovery scan' },
  { value: 'truenas', label: 'TrueNAS health' },
  { value: 'ha', label: 'Home Assistant health' },
  { value: 'summary', label: 'Summary refresh' },
];

const DISCOVERY_TOOL_OPTIONS = [
  { value: 'auto', label: 'Auto-select tool' },
  { value: 'nmap', label: 'Force nmap' },
  { value: 'arp-scan', label: 'Force arp-scan' },
];

const SCAN_TYPE_LABELS = {
  all: 'All scan types',
  discovery: 'Discovery',
  full_discovery: 'Discovery',
  ping: 'Ping',
  arp_snapshot: 'ARP snapshot',
  pfsense_traffic: 'pfSense traffic',
  truenas: 'TrueNAS',
  unifi: 'UniFi',
  ha: 'Home Assistant',
  onvif: 'ONVIF',
  summary: 'Summary refresh',
};

const SCAN_TYPE_OPTIONS = [
  { value: 'all', label: 'All scan types' },
  { value: 'full_discovery', label: 'Discovery' },
  { value: 'ping', label: 'Ping' },
  { value: 'arp_snapshot', label: 'ARP snapshot' },
  { value: 'pfsense_traffic', label: 'pfSense traffic' },
  { value: 'truenas', label: 'TrueNAS' },
  { value: 'unifi', label: 'UniFi' },
  { value: 'ha', label: 'Home Assistant' },
  { value: 'onvif', label: 'ONVIF' },
];

const formatScanType = (value) => SCAN_TYPE_LABELS[String(value || '').toLowerCase()] || String(value || 'Unknown');

const MANAGED_INTERFACE_TYPE_OPTIONS = [
  { value: 'pfsense', label: 'pfSense', description: 'pfSense firewall REST API' },
  { value: 'truenas_rest', label: 'TrueNAS', description: 'TrueNAS Scale / Core REST API' },
  { value: 'onvif', label: 'ONVIF Camera', description: 'ONVIF-compliant IP camera' },
  { value: 'isapi', label: 'Hik NVR', description: 'Hikvision NVR — ISAPI protocol' },
  { value: 'sonoff_diy', label: 'SONOFF DIY', description: 'SONOFF device in DIY / LAN mode — no credentials required' },
  { value: 'sonoff_lan', label: 'SONOFF LAN', description: 'SONOFF device in eWeLink LAN mode — API key required' },
];

const DEVICE_SORT_OPTIONS = [
  { value: 'lastSeenDesc', label: 'Last seen: newest first' },
  { value: 'lastSeenAsc', label: 'Last seen: oldest first' },
  { value: 'nameAsc', label: 'Name: A to Z' },
  { value: 'ipAsc', label: 'IP address' },
  { value: 'typeAsc', label: 'Type: A to Z' },
  { value: 'statusAsc', label: 'Status: A to Z' },
  { value: 'approvedFirst', label: 'Approved first' },
  { value: 'criticalFirst', label: 'Critical first' },
];

const DEVICE_HEADER_SORTS = {
  name: { asc: 'nameAsc', desc: 'nameAsc' },
  ip: { asc: 'ipAsc', desc: 'ipAsc' },
  type: { asc: 'typeAsc', desc: 'typeAsc' },
  status: { asc: 'statusAsc', desc: 'statusAsc' },
  approved: { asc: 'approvedFirst', desc: 'approvedFirst' },
  critical: { asc: 'criticalFirst', desc: 'criticalFirst' },
  lastSeen: { asc: 'lastSeenAsc', desc: 'lastSeenDesc' },
};

const SORTABLE_DEVICE_COLUMNS = new Set(['name', 'ip', 'type', 'status', 'approved', 'critical', 'lastSeen']);
const IMPLICIT_CRITICAL_DEVICE_TYPES = new Set(['unifi', 'unifi_ap', 'home_assistant', 'pfsense', 'truenas', 'nas']);
const IMPLICIT_CRITICAL_KEYWORDS = ['ubiquiti', 'unifi', 'home assistant', 'pfsense', 'truenas', 'nas'];

const getDeviceSortDirection = (sortOrder, columnKey) => {
  const mapping = DEVICE_HEADER_SORTS[columnKey];
  if (!mapping) {
    return 'none';
  }

  if (sortOrder === mapping.asc) {
    return 'ascending';
  }

  if (sortOrder === mapping.desc) {
    return 'descending';
  }

  return 'none';
};

const getNextDeviceSortOrder = (sortOrder, columnKey) => {
  const mapping = DEVICE_HEADER_SORTS[columnKey];
  if (!mapping) {
    return sortOrder;
  }

  return getDeviceSortDirection(sortOrder, columnKey) === 'ascending' ? mapping.desc : mapping.asc;
};

const isImplicitCriticalDevice = (device) => {
  const deviceType = String(device?.deviceType || '').toLowerCase();
  if (IMPLICIT_CRITICAL_DEVICE_TYPES.has(deviceType)) {
    return true;
  }

  const searchableText = [device?.manufacturer, device?.hostname, device?.notes]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');

  return IMPLICIT_CRITICAL_KEYWORDS.some((keyword) => searchableText.includes(keyword));
};

const getDeviceRowStateClass = (device) => {
  const severity = getSeverityClass(device?.status);
  if (severity === 'critical' || severity === 'high' || severity === 'warning') {
    return 'has-problem';
  }

  if (device?.critical) {
    return 'is-critical';
  }

  if (isImplicitCriticalDevice(device)) {
    return 'needs-critical';
  }

  return '';
};

const ManualScanControls = ({ onTriggered, compact = false, networkScope = {} }) => {
  const { user } = useAuth();
  const [triggering, setTriggering] = useState(false);
  const [triggerType, setTriggerType] = useState('discovery');
  const [discoveryTool, setDiscoveryTool] = useState('auto');
  const [error, setError] = useState('');
  const [triggerResult, setTriggerResult] = useState(null);
  const [debugTrace, setDebugTrace] = useState({ request: null, outcome: null });
  const [showDebugTrace, setShowDebugTrace] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    const handleDebugEvent = (event) => {
      const detail = event?.detail ?? null;
      if (!detail) {
        return;
      }

      setDebugTrace((current) => {
        if (detail.phase === 'request') {
          return {
            request: detail,
            outcome: null,
          };
        }

        if (detail.phase === 'response' || detail.phase === 'error') {
          return {
            request: current.request,
            outcome: detail,
          };
        }

        return current;
      });
    };

    window.addEventListener('studioaeye:network-scan-trigger-debug', handleDebugEvent);
    return () => {
      window.removeEventListener('studioaeye:network-scan-trigger-debug', handleDebugEvent);
    };
  }, []);

  const handleTriggerScan = async () => {
    try {
      setError('');
      setTriggerResult(null);
      setTriggering(true);

      const payload = { type: triggerType };
      if (triggerType === 'discovery' && discoveryTool !== 'auto') {
        payload.tool = discoveryTool;
      }

      const response = await networkApi.triggerScan(payload, networkScope);
      setTriggerResult(normalizeTriggerResult(extractData(response)));

      if (onTriggered) {
        await onTriggered();
      }
    } catch (err) {
      console.error('Failed to trigger scan:', err);
      setError(getNetworkErrorMessage(err, 'Failed to trigger manual scan. This action may require super admin access.'));
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div className={`network-trigger-panel${compact ? ' compact' : ''}`}>
      <div className="network-trigger-controls">
        <select
          value={triggerType}
          onChange={(event) => setTriggerType(event.target.value)}
          className="network-select"
          aria-label="Manual scan type"
        >
          {TRIGGER_SCAN_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        {triggerType === 'discovery' ? (
          <select
            value={discoveryTool}
            onChange={(event) => setDiscoveryTool(event.target.value)}
            className="network-select"
            aria-label="Discovery scan tool"
          >
            {DISCOVERY_TOOL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        ) : null}

        <button className="network-primary-button" disabled={triggering} onClick={handleTriggerScan}>
          {triggering ? 'Triggering...' : 'Trigger manual scan'}
        </button>
      </div>

      <div className="network-detail-block">
        <span>What this runs</span>
        <p>{TRIGGER_SCAN_DESCRIPTIONS[triggerType]}</p>
      </div>

      {isSuperAdmin ? (
        <label className="network-debug-toggle">
          <input
            type="checkbox"
            checked={showDebugTrace}
            onChange={(event) => setShowDebugTrace(event.target.checked)}
          />
          <span>Show debug trace</span>
        </label>
      ) : null}

      {error ? <div className="network-error-banner">{error}</div> : null}
      {triggerResult ? (
        <div className="network-trigger-result">
          <div className="network-loading-panel">{triggerResult.message}</div>
          <div className="network-detail-grid">
            <div><span>Type</span><strong>{formatScanType(triggerResult.scanType || triggerType)}</strong></div>
            <div><span>Status</span><strong>{triggerResult.status || 'Queued'}</strong></div>
            <div><span>Triggered</span><strong>{formatDateTime(triggerResult.triggeredAt)}</strong></div>
            <div><span>Subnet</span><strong>{triggerResult.subnet || 'Configured default'}</strong></div>
            <div><span>Tool</span><strong>{triggerResult.tool || 'auto'}</strong></div>
            <div><span>Requested Tool</span><strong>{triggerResult.requestedTool || 'auto'}</strong></div>
            <div><span>Devices Found</span><strong>{triggerResult.devicesFound}</strong></div>
            <div><span>New Devices</span><strong>{triggerResult.newDevices}</strong></div>
            <div><span>Anomalies</span><strong>{triggerResult.anomaliesFound}</strong></div>
          </div>
        </div>
      ) : null}

      {isSuperAdmin && showDebugTrace && (debugTrace.request || debugTrace.outcome) ? (
        <div className="network-debug-panel">
          <div className="network-debug-header">
            <strong>Debug Trace</strong>
            <span>Latest /api/network/scans/trigger payloads</span>
          </div>

          {debugTrace.request ? (
            <div className="network-debug-block">
              <span>Request</span>
              <pre>{JSON.stringify(debugTrace.request, null, 2)}</pre>
            </div>
          ) : null}

          {debugTrace.outcome ? (
            <div className="network-debug-block">
              <span>{debugTrace.outcome.phase === 'error' ? 'Error response' : 'Response'}</span>
              <pre>{JSON.stringify(debugTrace.outcome, null, 2)}</pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const NetworkOverviewContent = ({ summary, anomalies, scans }) => (
  <>
    <div className="network-summary-grid">
      <SummaryCard label="Overall Status" value={String(summary.status || 'unknown').toUpperCase()} tone={getSeverityClass(summary.status)} helper={summary.lastScanAt ? `Updated ${formatDateTime(summary.lastScanAt)}` : 'Awaiting first scan'} />
      <SummaryCard label="Known Devices" value={summary.deviceCount} helper={`${summary.onlineDevices} online`} />
      <SummaryCard label="Critical Devices" value={summary.criticalDevices} helper="Flagged for active monitoring" />
      <SummaryCard label="Open Anomalies" value={summary.unresolvedAnomalies} tone={summary.unresolvedAnomalies > 0 ? 'warning' : 'info'} helper="Requires admin review" />
    </div>

    <div className="network-panel-grid two-up">
      <section className="network-panel">
        <div className="network-panel-header">
          <h2>Infrastructure Health</h2>
          <Link to="/app/admin/network/health" className="network-text-link">View all</Link>
        </div>
        {summary.systems.length === 0 ? (
          <EmptyState title="No subsystem data" text="The network gateway has not returned subsystem health yet." />
        ) : (
          <div className="network-system-grid compact">
            {summary.systems.map((system) => (
              <div key={system.key} className={`network-system-tile severity-${getSeverityClass(system.status)}`}>
                <div>
                  <strong>{system.name}</strong>
                  <span>{String(system.status || 'unknown').toUpperCase()}</span>
                </div>
                <p>{system.detail || 'No additional detail available.'}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="network-panel">
        <div className="network-panel-header">
          <h2>Recent Anomalies</h2>
          <Link to="/app/admin/network/anomalies" className="network-text-link">Open log</Link>
        </div>
        {anomalies.length === 0 ? (
          <EmptyState title="No anomalies" text="No recent anomalies were returned by the backend." />
        ) : (
          <div className="network-activity-list">
            {anomalies.slice(0, 5).map((anomaly) => (
              <Link key={anomaly.id || `${anomaly.type}-${anomaly.detectedAt}`} to={`/app/admin/network/anomalies/${anomaly.id || ''}`} className={`network-activity-item severity-${getSeverityClass(anomaly.severity)}`}>
                <div>
                  <strong>{anomaly.title}</strong>
                  <p>{anomaly.detail || anomaly.type}</p>
                </div>
                <span>{formatDateTime(anomaly.detectedAt)}</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>

    <section className="network-panel">
      <div className="network-panel-header">
        <h2>Recent Scan History</h2>
        <Link to="/app/admin/network/scans" className="network-text-link">See all scans</Link>
      </div>
      {scans.length === 0 ? (
        <EmptyState title="No scans yet" text="The network gateway has not returned any scan history records." />
      ) : (
        <div className="network-table-wrap">
          <table className="network-table">
            <thead>
              <tr>
                <th>Scan</th>
                <th>Status</th>
                <th>Devices</th>
                <th>New</th>
                <th>Anomalies</th>
                <th>Duration</th>
                <th>Completed</th>
              </tr>
            </thead>
            <tbody>
              {scans.slice(0, 6).map((scan) => (
                <tr key={scan.id || `${scan.type}-${scan.startedAt}`}>
                  <td>{formatScanType(scan.type)}</td>
                  <td><span className={`network-pill severity-${getSeverityClass(scan.status)}`}>{scan.status}</span></td>
                  <td>{scan.devicesFound}</td>
                  <td>{scan.newDevices}</td>
                  <td>{scan.anomaliesFound}</td>
                  <td>{formatDuration(scan.durationMs)}</td>
                  <td>{formatDateTime(scan.completedAt || scan.startedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  </>
);

const NetworkDevicesTable = ({ devices, savingId, onUpdateDevice, onOpenDeviceDetail, onStartOnboarding, emptyText, dense = false, sortOrder, onSortChange }) => {
  const handleRowKeyDown = (event, deviceId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenDeviceDetail(deviceId);
    }
  };

  const handleActionClick = (event, callback) => {
    event.stopPropagation();
    callback();
  };

  if (devices.length === 0) {
    return <EmptyState title="No devices returned" text={emptyText} />;
  }

  const renderHeader = (label, columnKey) => {
    if (!SORTABLE_DEVICE_COLUMNS.has(columnKey) || !onSortChange) {
      return <th>{label}</th>;
    }

    const sortDirection = getDeviceSortDirection(sortOrder, columnKey);
    const indicator = sortDirection === 'ascending' ? '▲' : sortDirection === 'descending' ? '▼' : '↕';

    return (
      <th aria-sort={sortDirection}>
        <button
          type="button"
          className={`network-table-sort${sortDirection !== 'none' ? ' is-active' : ''}`}
          onClick={() => onSortChange(getNextDeviceSortOrder(sortOrder, columnKey))}
        >
          <span>{label}</span>
          <span className="network-table-sort-indicator" aria-hidden="true">{indicator}</span>
        </button>
      </th>
    );
  };

  return (
    <div className="network-table-wrap">
      <table className={`network-table${dense ? ' network-table-dense' : ''}`}>
        <thead>
          <tr>
            {renderHeader('Device', 'name')}
            {renderHeader('IP', 'ip')}
            <th>MAC</th>
            {renderHeader('Type', 'type')}
            {renderHeader('Status', 'status')}
            {renderHeader('Approved', 'approved')}
            {renderHeader('Critical', 'critical')}
            {renderHeader('Last Seen', 'lastSeen')}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((device) => (
            (() => {
              const webInterfaceUrl = getDeviceWebInterfaceUrl(device);
              const displayStatus = getDeviceDisplayStatus(device);

              return (
            <tr
              key={device.id || `${device.macAddress}-${device.ipAddress}`}
              className={`network-table-row-clickable ${getDeviceRowStateClass(device)}`.trim()}
              onClick={() => onOpenDeviceDetail(device.id)}
              onKeyDown={(event) => handleRowKeyDown(event, device.id)}
              tabIndex={0}
              role="link"
              aria-label={`Open details for ${device.hostname || device.ipAddress || 'device'}`}
            >
              <td>
                <div className="network-device-cell">
                  <strong>{device.hostname || device.ipAddress || 'Unknown device'}</strong>
                  <span>{device.manufacturer || 'Unknown manufacturer'}</span>
                </div>
              </td>
              <td>{device.ipAddress || 'No IP'}</td>
              <td>{device.macAddress || 'No MAC'}</td>
              <td>{device.deviceType}</td>
              <td><span className={`network-pill severity-${getSeverityClass(displayStatus)}`}>{displayStatus}</span></td>
              <td>{formatBoolean(device.approved)}</td>
              <td>{formatBoolean(device.critical)}</td>
              <td>{formatDateTime(device.lastSeen)}</td>
              <td>
                <div className="network-inline-actions">
                  <button className="network-secondary-button" disabled={savingId === device.id} onClick={(event) => handleActionClick(event, () => onUpdateDevice(device, { is_approved: !device.approved }))}>
                    {device.approved ? 'Unapprove' : 'Approve'}
                  </button>
                  <button className="network-primary-button" onClick={(event) => handleActionClick(event, () => onStartOnboarding?.(device.id))}>
                    Onboard
                  </button>
                  <button className="network-secondary-button" disabled={savingId === device.id} onClick={(event) => handleActionClick(event, () => onUpdateDevice(device, { is_critical: !device.critical }))}>
                    {device.critical ? 'Clear Critical' : 'Flag Critical'}
                  </button>
                  {webInterfaceUrl ? (
                    <a
                      className="network-secondary-link"
                      href={webInterfaceUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      Open UI
                    </a>
                  ) : null}
                </div>
              </td>
            </tr>
              );
            })()
          ))}
        </tbody>
      </table>
    </div>
  );
};

const useNetworkRefresh = (callback) => {
  useEffect(() => {
    return undefined;
  }, [callback]);
};

const NETWORK_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'devices', label: 'Devices' },
  { id: 'scans', label: 'Scans' },
  { id: 'packet-stats', label: 'Packet Stats' },
];

const AdminNetworkTabbedPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleStepUpError, stepUpDialog } = useNetworkStepUp();
  const networkScope = useNetworkOwnerOrgScope(handleStepUpError);
  const requestedTab = searchParams.get('tab');
  const activeTab = NETWORK_TABS.some((tab) => tab.id === requestedTab) ? requestedTab : 'overview';
  // Multi-node: require explicit installationUuid (fail-closed; never guess).
  const needsNodeSelection = (networkScope.gatewaySources?.length || 0) > 1
    && !networkScope.selectedInstallationUuid;
  const canLoadNetwork = !networkScope.loading
    && !networkScope.error
    && Boolean(networkScope.ownerOrgId)
    && Boolean(networkScope.selectedInstallationUuid)
    && !needsNodeSelection;

  const selectTab = (tabId) => {
    const extra = tabId === 'overview' ? {} : { tab: tabId };
    navigate(networkScope.buildPath('/app/admin/network', extra));
  };

  return (
    <Layout>
      <div className="network-page">
        <SectionHeader
          title="Network"
          subtitle="Monitor Mycelium-linked network health, review discovered devices, and inspect scan history from one workspace."
        />
        {stepUpDialog}
        <NetworkOwnerOrgPanel
          {...networkScope}
          onOwnerOrgIdChange={networkScope.selectOwnerOrgId}
          onInstallationUuidChange={networkScope.selectInstallationUuid}
        />

        <div className="network-tabs" role="tablist" aria-label="Network sections">
          {NETWORK_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`network-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => selectTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {!networkScope.loading && needsNodeSelection ? (
          <EmptyState
            title="Select a Mycelium node"
            text="This organisation has more than one linked Mycelium node. Choose one above — the workspace never merges inventories or picks a default."
          />
        ) : null}
        {!networkScope.loading && !canLoadNetwork && !needsNodeSelection ? (
          <EmptyState title="Network link unavailable" text="Link Mycelium to the selected organisation or reconnect the service-link tunnel." />
        ) : null}
        {canLoadNetwork && activeTab === 'overview' ? <AdminNetworkOverview embedded networkScope={networkScope.scopeParams} /> : null}
        {canLoadNetwork && activeTab === 'devices' ? (
          <AdminNetworkDeviceListViewer
            embedded
            networkScope={networkScope.scopeParams}
            gatewaySource={networkScope.gatewaySource}
            selectedOrganisation={networkScope.selectedOrganisation}
          />
        ) : null}
        {canLoadNetwork && activeTab === 'scans' ? <AdminNetworkScans embedded networkScope={networkScope.scopeParams} /> : null}
        {canLoadNetwork && activeTab === 'packet-stats' ? <AdminPacketStats networkScope={networkScope.scopeParams} /> : null}
      </div>
    </Layout>
  );
};

const AdminNetworkOverview = ({ embedded = false, networkScope = {} }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(normalizeSummary({}));
  const [anomalies, setAnomalies] = useState([]);
  const [scans, setScans] = useState([]);
  const { handleStepUpError, stepUpDialog } = useNetworkStepUp();

  const loadOverview = async () => {
    try {
      setError('');
      const [summaryResponse, anomaliesResponse, scansResponse] = await Promise.all([
        networkApi.getHealthSummary(networkScope),
        networkApi.getAnomalies({ ...networkScope, limit: 5 }),
        networkApi.getScans({ ...networkScope, limit: 6 }),
      ]);

      setSummary(normalizeSummary(extractData(summaryResponse)));
      setAnomalies(asArray(extractData(anomaliesResponse), ['items', 'rows', 'anomalies']).map(normalizeAnomaly));
      setScans(asArray(extractData(scansResponse), ['items', 'rows', 'scans']).map(normalizeScan));
    } catch (err) {
      console.error('Failed to load network overview:', err);
      if (handleStepUpError(err, loadOverview)) {
        setError('');
        return;
      }
      setError(getNetworkErrorMessage(err, 'Failed to load network monitor overview.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, [networkScope.ownerOrgId, networkScope.installationUuid]);

  useNetworkRefresh(() => {
    loadOverview();
  });

  const content = (
      <div className="network-page">
        <SectionHeader
          title="Network Monitor"
          subtitle="Mycelium-linked network health, anomaly tracking, and scan activity for administrators."
          actions={(
            <>
              <Link to="/app/admin/network?tab=devices" className="network-secondary-link">Device Viewer</Link>
              <Link to="/app/admin/network?tab=scans" className="network-primary-link">Scan history</Link>
            </>
          )}
        />
        {stepUpDialog}
        <section className="network-panel">
          <div className="network-panel-header">
            <h2>Manual Scan Trigger</h2>
            <span className="network-summary-helper">Sends a scoped gateway request</span>
          </div>
          <ManualScanControls onTriggered={loadOverview} networkScope={networkScope} />
        </section>
        {loading ? <LoadingPanel /> : null}
        {error ? <div className="network-error-banner">{error}</div> : null}
        {!loading ? <NetworkOverviewContent summary={summary} anomalies={anomalies} scans={scans} /> : null}
      </div>
  );

  return embedded ? content : <Layout>{content}</Layout>;
};

const getDeviceRegistryEmptyText = ({ search, statusFilter, typeFilter, networkScope, gatewaySource, selectedOrganisation }) => {
  const hasFilters = Boolean(search.trim()) || statusFilter !== 'all' || typeFilter !== 'all';
  if (hasFilters) {
    return 'No devices match the current filters. Clear the filters to see every device returned by the Mycelium gateway.';
  }

  if (!networkScope.ownerOrgId) {
    return 'Select an organisation before loading the Mycelium device registry.';
  }

  const organisationText = selectedOrganisation?.name
    ? ` for ${selectedOrganisation.name}`
    : ' for the selected organisation';
  const sourceText = gatewaySource?.label
    ? ` from ${gatewaySource.label}`
    : ' from the Mycelium gateway';
  const stateText = gatewaySource?.state
    ? ` Current gateway state: ${formatLabel(gatewaySource.state)}.`
    : '';

  return `No devices were returned${sourceText}${organisationText}.${stateText} Complete network access 2FA or confirm the selected organisation has an online linked Mycelium service.`;
};

const AdminNetworkDeviceListViewer = ({ embedded = false, networkScope = {}, gatewaySource = null, selectedOrganisation = null }) => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('lastSeenDesc');
  const [savingId, setSavingId] = useState(null);
  const { handleStepUpError, stepUpDialog } = useNetworkStepUp();

  const loadDevices = async () => {
    try {
      setError('');
      setLoading(true);
      const response = await networkApi.getDevices({ ...networkScope, limit: 200, offset: 0 });
      const payload = extractData(response);
      setDevices(asArray(payload, ['items', 'rows', 'devices']).map(normalizeDevice));
    } catch (err) {
      console.error('Failed to load network devices:', err);
      if (handleStepUpError(err, loadDevices)) {
        setError('');
        return;
      }
      setError(getNetworkErrorMessage(err, 'Failed to load devices.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, [networkScope.ownerOrgId, networkScope.installationUuid]);

  useNetworkRefresh(() => {
    loadDevices();
  });

  const updateDevice = async (device, patch) => {
    try {
      setSavingId(device.id);
      await networkApi.updateDevice(device.id, patch, networkScope);
      await loadDevices();
    } catch (err) {
      console.error('Failed to update device:', err);
      if (handleStepUpError(err, () => updateDevice(device, patch))) {
        setError('');
        return;
      }
      setError(getNetworkErrorMessage(err, 'Failed to update device.'));
    } finally {
      setSavingId(null);
    }
  };

  const openDeviceDetail = (deviceId, options = {}) => {
    if (!deviceId) {
      return;
    }

    navigate(buildNetworkPath(
      `/app/admin/network/devices/${deviceId}`,
      networkScope,
      options.onboarding ? { onboarding: '1' } : {},
    ));
  };

  const startOnboarding = (deviceId) => {
    openDeviceDetail(deviceId, { onboarding: true });
  };

  const normalizedSearch = search.trim().toLowerCase();
  const filteredDevices = devices.filter((device) => {
    if (statusFilter !== 'all' && getDeviceDisplayStatus(device) !== statusFilter) {
      return false;
    }

    if (typeFilter !== 'all' && String(device.deviceType || '').toLowerCase() !== typeFilter) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    const searchableFields = [
      device.hostname,
      device.ipAddress,
      device.macAddress,
      device.manufacturer,
      device.deviceType,
      getDeviceDisplayStatus(device),
      device.notes,
    ];

    return searchableFields.some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
  });

  const sortedDevices = [...filteredDevices].sort((left, right) => {
    switch (sortOrder) {
      case 'lastSeenAsc':
        return new Date(left.lastSeen || 0).getTime() - new Date(right.lastSeen || 0).getTime();
      case 'nameAsc':
        return compareText(left.hostname || left.ipAddress, right.hostname || right.ipAddress);
      case 'ipAsc':
        return compareIpAddress(left.ipAddress, right.ipAddress);
      case 'typeAsc':
        return compareText(left.deviceType, right.deviceType);
      case 'statusAsc':
        return compareText(left.status, right.status);
      case 'approvedFirst':
        if (left.approved !== right.approved) {
          return left.approved ? -1 : 1;
        }
        return compareText(left.hostname || left.ipAddress, right.hostname || right.ipAddress);
      case 'criticalFirst':
        if (left.critical !== right.critical) {
          return left.critical ? -1 : 1;
        }
        return compareText(left.hostname || left.ipAddress, right.hostname || right.ipAddress);
      case 'lastSeenDesc':
      default:
        return new Date(right.lastSeen || 0).getTime() - new Date(left.lastSeen || 0).getTime();
    }
  });

  const content = (
      <div className="network-page">
        <SectionHeader
          title="Device Registry"
          subtitle="Canonical registry route for discovered devices, onboarding status, and entry into documented device detail workflows."
          actions={<Link to="/app/admin/network" className="network-secondary-link">Back to monitor</Link>}
        />
        {stepUpDialog}

        <div className="network-summary-grid">
          <SummaryCard label="Visible Devices" value={sortedDevices.length} helper={`${devices.length} total loaded`} />
          <SummaryCard label="Approved Devices" value={sortedDevices.filter((device) => device.approved).length} helper="Within current result set" />
          <SummaryCard label="Needs Onboarding" value={sortedDevices.filter((device) => !device.approved || device.managementState !== 'managed').length} helper="Review or add managed interfaces" />
          <SummaryCard label="Critical Devices" value={sortedDevices.filter((device) => device.critical).length} helper="Within current result set" />
        </div>

        <div className="network-toolbar">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="network-input"
            placeholder="Search by IP, MAC, host, or manufacturer"
          />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="network-select">
            <option value="all">All statuses</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="healthy">Healthy</option>
            <option value="degraded">Degraded</option>
            <option value="unknown">Unknown</option>
          </select>
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className="network-select">
            <option value="all">All types</option>
            <option value="tuya">Tuya</option>
            <option value="truenas">TrueNAS</option>
            <option value="unifi_ap">UniFi AP</option>
            <option value="home_assistant">Home Assistant</option>
            <option value="pfsense">pfSense</option>
            <option value="onvif_camera">ONVIF camera</option>
            <option value="unknown">Unknown</option>
          </select>
          <select value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} className="network-select">
            {DEVICE_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {error ? <div className="network-error-banner">{error}</div> : null}
        {loading ? <LoadingPanel title="Loading devices..." /> : null}
        {!loading ? (
          <NetworkDevicesTable
            devices={sortedDevices}
            savingId={savingId}
            onUpdateDevice={updateDevice}
            onOpenDeviceDetail={openDeviceDetail}
            onStartOnboarding={startOnboarding}
            sortOrder={sortOrder}
            onSortChange={setSortOrder}
            emptyText={getDeviceRegistryEmptyText({
              search,
              statusFilter,
              typeFilter,
              networkScope,
              gatewaySource,
              selectedOrganisation,
            })}
            dense
          />
        ) : null}
      </div>
  );

  return embedded ? content : <Layout>{content}</Layout>;
};

const TrueNASHealthCard = ({ health }) => {
  const pools = health.pools || [];
  const disks = health.disks || health.smart || [];
  const services = health.services || [];
  const alerts = health.alerts || [];
  const ifaces = health.interfaces || [];
  const hasContent = health.hostname || pools.length || disks.length || services.length || alerts.length || ifaces.length;

  if (!hasContent) {
    return <p className="network-supporting-text">TrueNAS health data received but no displayable fields found yet.</p>;
  }

  return (
    <div className="network-provider-health">
      <div className="network-detail-grid">
        {health.hostname ? <div><span>Hostname</span><strong>{health.hostname}</strong></div> : null}
        {health.version ? <div><span>Version</span><strong>{health.version}</strong></div> : null}
        {health.uptime ? <div><span>Uptime</span><strong>{health.uptime}</strong></div> : null}
        {health.loadAvg != null ? <div><span>Load avg</span><strong>{String(health.loadAvg)}</strong></div> : null}
        {health.systemState ? <div><span>System state</span><strong>{health.systemState}</strong></div> : null}
      </div>

      {pools.length > 0 ? (
        <>
          <div className="network-detail-block"><span>Storage Pools</span></div>
          <div className="network-managed-interface-list">
            {pools.map((pool, i) => (
              <div key={pool.name || i} className="network-managed-interface-card">
                <div className="network-managed-interface-header">
                  <div><strong>{pool.name}</strong><span>{pool.status || 'unknown'}</span></div>
                  <span className={`network-pill severity-${pool.status === 'ONLINE' ? 'ok' : 'warning'}`}>{pool.status}</span>
                </div>
                <div className="network-detail-grid">
                  {pool.size ? <div><span>Total</span><strong>{pool.size}</strong></div> : null}
                  {pool.usedPct != null ? <div><span>Used</span><strong>{pool.usedPct}%</strong></div> : null}
                  {pool.scrubState ? <div><span>Scrub</span><strong>{pool.scrubState}</strong></div> : null}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {disks.length > 0 ? (
        <>
          <div className="network-detail-block"><span>SMART Results</span></div>
          <div className="network-managed-interface-list">
            {disks.map((disk, i) => (
              <div key={disk.name || i} className="network-managed-interface-card">
                <div className="network-managed-interface-header">
                  <div><strong>{disk.name}</strong><span>{disk.result || disk.status}</span></div>
                  <span className={`network-pill severity-${(disk.result || disk.status || '').toUpperCase() === 'SUCCESS' ? 'ok' : 'warning'}`}>{disk.result || disk.status}</span>
                </div>
                {disk.message ? <p>{disk.message}</p> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {ifaces.length > 0 ? (
        <>
          <div className="network-detail-block"><span>Network Interfaces</span></div>
          <div className="network-managed-interface-list">
            {ifaces.map((iface, i) => (
              <div key={iface.name || i} className="network-managed-interface-card">
                <div className="network-managed-interface-header">
                  <div><strong>{iface.name}</strong><span>{iface.state}</span></div>
                  <span className={`network-pill severity-${iface.state === 'UP' ? 'ok' : 'warning'}`}>{iface.state}</span>
                </div>
                <div className="network-detail-grid">
                  {iface.speed ? <div><span>Speed</span><strong>{iface.speed}</strong></div> : null}
                  {iface.rxRate ? <div><span>In</span><strong>{iface.rxRate}</strong></div> : null}
                  {iface.txRate ? <div><span>Out</span><strong>{iface.txRate}</strong></div> : null}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {services.length > 0 ? (
        <>
          <div className="network-detail-block"><span>Services</span></div>
          <div className="network-detail-grid">
            {services.map((svc, i) => (
              <div key={svc.name || i}>
                <span>{svc.name}</span>
                <strong className={svc.state === 'RUNNING' || svc.state === 'running' ? '' : 'network-text-muted'}>{svc.state}</strong>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {alerts.length > 0 ? (
        <>
          <div className="network-detail-block"><span>Active Alerts ({alerts.length})</span></div>
          <div className="network-managed-interface-list">
            {alerts.map((alert, i) => (
              <div key={alert.id || i} className="network-managed-interface-card">
                <div className="network-managed-interface-header">
                  <div><strong>{alert.level || 'ALERT'}</strong></div>
                  <span className={`network-pill severity-${getSeverityClass(alert.level || 'warning')}`}>{alert.level}</span>
                </div>
                {alert.text || alert.message ? <p>{alert.text || alert.message}</p> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

const SparklineGraph = ({ values = [], width = 120, height = 28, color = 'currentColor', label }) => {
  if (!values || values.length < 2) {
    return <span className="network-sparkline-empty">–</span>;
  }
  const max = Math.max(...values, 0.001);
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - (v / max) * (height - 2) - 1;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <div className="network-sparkline">
      {label ? <span className="network-sparkline-label">{label}</span> : null}
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" points={points} />
      </svg>
      <span className="network-sparkline-peak">{Number(values[values.length - 1]).toFixed(1)}</span>
    </div>
  );
};

const PfSenseHealthCard = ({ health }) => {
  const ifaces = health.interfaces || [];
  const gateway = health.wanGateway || health.gateway || null;
  const ipsec = health.ipsec || {};
  const openvpn = health.openvpn || health.openVpn || health.open_vpn || {};
  const trafficGraphs = health.trafficGraphs || health.traffic || {};
  const wanTraffic = asArray(trafficGraphs.wan);
  const lanTraffic = asArray(trafficGraphs.lan);
  const hasTraffic = wanTraffic.length > 0 || lanTraffic.length > 0;
  const idsAlerts = asArray((health.idsAlertSummary || {}).recent || health.alerts || health.ids_alerts);

  const hasContent =
    gateway ||
    ifaces.length > 0 ||
    idsAlerts.length > 0 ||
    ipsec.up != null ||
    openvpn.up != null ||
    hasTraffic;

  if (!hasContent) {
    return <p className="network-supporting-text">pfSense health data received but no displayable fields found yet.</p>;
  }

  return (
    <div className="network-provider-health">
      {/* Gateway */}
      {gateway ? (
        <div className="network-detail-grid">
          <div><span>WAN Gateway</span><strong>{gateway.status}</strong></div>
          {gateway.delayMs != null ? <div><span>RTT</span><strong>{gateway.delayMs} ms</strong></div> : null}
          {gateway.lossPct != null ? <div><span>Loss</span><strong>{gateway.lossPct}%</strong></div> : null}
          {gateway.jitterMs != null ? <div><span>Jitter</span><strong>{gateway.jitterMs} ms</strong></div> : null}
        </div>
      ) : null}

      {/* Interfaces */}
      {ifaces.length > 0 ? (
        <>
          <div className="network-detail-block"><span>Interfaces</span></div>
          <div className="network-managed-interface-list">
            {ifaces.map((iface, i) => {
              const ifaceUp = (iface.status || iface.state || '').toUpperCase() === 'UP';
              return (
                <div key={iface.name || i} className="network-managed-interface-card">
                  <div className="network-managed-interface-header">
                    <div>
                      <strong>{iface.name}</strong>
                      {iface.description ? <span>{iface.description}</span> : null}
                    </div>
                    <span className={`network-pill severity-${ifaceUp ? 'ok' : 'warning'}`}>{iface.status || iface.state}</span>
                  </div>
                  <div className="network-detail-grid">
                    {iface.inMbps != null ? <div><span>In</span><strong>{iface.inMbps} Mbps</strong></div> : null}
                    {iface.outMbps != null ? <div><span>Out</span><strong>{iface.outMbps} Mbps</strong></div> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      {/* IPsec + OpenVPN — two-column side-by-side */}
      {(ipsec.up != null || openvpn.up != null) ? (
        <div className="network-pfsense-vpn-grid">
          {ipsec.up != null ? (
            <div className="network-pfsense-vpn-panel">
              <div className="network-detail-block"><span>IPsec Status</span></div>
              <div className="network-detail-grid">
                <div><span>Tunnels up</span><strong>{ipsec.up}</strong></div>
                {ipsec.down != null ? (
                  <div>
                    <span>Tunnels down</span>
                    <strong className={ipsec.down > 0 ? 'network-text-warning' : ''}>{ipsec.down}</strong>
                  </div>
                ) : null}
              </div>
              {asArray(ipsec.tunnels).length > 0 ? (
                <div className="network-pfsense-vpn-detail">
                  {asArray(ipsec.tunnels).map((tunnel, i) => {
                    const tunnelUp = (tunnel.status || tunnel.state || '').toLowerCase() === 'up';
                    return (
                      <div key={tunnel.name || i} className="network-pfsense-vpn-entry">
                        <span>{tunnel.name || `Tunnel ${i + 1}`}</span>
                        <span className={`network-pill severity-${tunnelUp ? 'ok' : 'warning'}`}>{tunnel.status || tunnel.state}</span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          {openvpn.up != null ? (
            <div className="network-pfsense-vpn-panel">
              <div className="network-detail-block"><span>OpenVPN Status</span></div>
              <div className="network-detail-grid">
                <div><span>Instances up</span><strong>{openvpn.up}</strong></div>
                {openvpn.down != null ? (
                  <div>
                    <span>Instances down</span>
                    <strong className={openvpn.down > 0 ? 'network-text-warning' : ''}>{openvpn.down}</strong>
                  </div>
                ) : null}
              </div>
              {asArray(openvpn.instances).length > 0 ? (
                <div className="network-pfsense-vpn-detail">
                  {asArray(openvpn.instances).map((inst, i) => {
                    const instUp = (inst.status || inst.state || '').toLowerCase() === 'up';
                    return (
                      <div key={inst.name || i} className="network-pfsense-vpn-entry">
                        <span>{inst.name || `Instance ${i + 1}`}{inst.kind ? ` (${inst.kind})` : ''}</span>
                        <span className={`network-pill severity-${instUp ? 'ok' : 'warning'}`}>{inst.status || inst.state}</span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* WAN / LAN Traffic */}
      {hasTraffic ? (
        <>
          <div className="network-detail-block"><span>WAN / LAN Traffic</span></div>
          <div className="network-pfsense-traffic-graphs">
            {[['WAN', wanTraffic], ['LAN', lanTraffic]].map(([label, entries]) =>
              entries.length > 0 ? (
                <div key={label} className="network-pfsense-traffic-row">
                  <div className="network-pfsense-traffic-label">
                    <strong>{label}</strong>
                  </div>
                  <div className="network-detail-grid">
                    {entries.map((entry, i) => (
                      <React.Fragment key={entry.interface || i}>
                        {entry.inMbps != null ? <div><span>{entry.interface || label} In</span><strong>{Number(entry.inMbps).toFixed(2)} Mbps</strong></div> : null}
                        {entry.outMbps != null ? <div><span>{entry.interface || label} Out</span><strong>{Number(entry.outMbps).toFixed(2)} Mbps</strong></div> : null}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ) : null
            )}
          </div>
        </>
      ) : null}

      {/* IDS / Firewall */}
      {idsAlerts.length > 0 ? (
        <>
          <div className="network-detail-block"><span>IDS / Firewall Alerts ({idsAlerts.length})</span></div>
          <div className="network-managed-interface-list">
            {idsAlerts.map((alert, i) => (
              <div key={alert.id || i} className="network-managed-interface-card">
                <p>{alert.message || alert.text || JSON.stringify(alert)}</p>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

const SONOFF_SIGNAL_QUALITY_LABEL = {
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair — monitor for drop-outs',
  poor: 'Poor — risk of missed commands',
};

const getCanonicalSonoffProbeState = (probeResult) => {
  const state = probeResult?.state;
  if (
    probeResult?.result !== 'success'
    || probeResult?.health_state !== 'healthy'
    || !state
    || !['on', 'off'].includes(state.switch)
    || !['on', 'off', 'stay'].includes(state.startup)
    || !['on', 'off'].includes(state.pulse)
    || !Number.isFinite(state.pulseWidth)
    || state.pulseWidth < 0
    || !Number.isFinite(state.signalStrengthDbm)
    || !Object.prototype.hasOwnProperty.call(SONOFF_SIGNAL_QUALITY_LABEL, state.signalQuality)
    || typeof state.otaUnlock !== 'boolean'
  ) {
    return null;
  }

  return {
    switch: state.switch,
    startup: state.startup,
    pulse: state.pulse,
    pulseWidth: state.pulseWidth,
    signalStrengthDbm: state.signalStrengthDbm,
    signalQuality: state.signalQuality,
    otaUnlock: state.otaUnlock,
  };
};

const SonoffHealthCard = ({ health }) => {
  if (!health) {
    return <p className="network-supporting-text">No SONOFF health data yet. Use Re-test to trigger an immediate probe.</p>;
  }

  const qualityLabel = SONOFF_SIGNAL_QUALITY_LABEL[health.signalQuality] || health.signalQuality;

  return (
    <div className="network-provider-health">
      <div className="network-detail-grid">
        {health.switch != null ? <div><span>Switch</span><strong>{health.switch === 'on' ? 'ON' : 'OFF'}</strong></div> : null}
        {health.startup ? <div><span>Power-on state</span><strong>{health.startup}</strong></div> : null}
        {health.pulse != null ? <div><span>Pulse mode</span><strong>{health.pulse === 'on' ? 'ON' : 'OFF'}</strong></div> : null}
        {health.pulseWidth != null ? <div><span>Pulse width</span><strong>{health.pulseWidth} ms</strong></div> : null}
        {health.signalStrengthDbm != null ? <div><span>WiFi RSSI</span><strong>{health.signalStrengthDbm} dBm</strong></div> : null}
        {qualityLabel ? <div><span>WiFi quality</span><strong>{qualityLabel}</strong></div> : null}
        {health.otaUnlock != null ? <div><span>OTA unlock</span><strong>{health.otaUnlock ? 'Unlocked' : 'Locked'}</strong></div> : null}
      </div>
    </div>
  );
};

const NvrHealthCard = ({ health }) => {
  const hasContent = health.deviceName || health.model || health.firmwareVersion || health.channelCount || health.recordingState || health.diskState;

  if (!hasContent) {
    return <p className="network-supporting-text">NVR health data received but no displayable fields found yet.</p>;
  }

  return (
    <div className="network-provider-health">
      <div className="network-detail-grid">
        {health.deviceName ? <div><span>Device name</span><strong>{health.deviceName}</strong></div> : null}
        {health.model ? <div><span>Model</span><strong>{health.model}</strong></div> : null}
        {health.firmwareVersion ? <div><span>Firmware</span><strong>{health.firmwareVersion}</strong></div> : null}
        {health.channelCount != null ? <div><span>Channels</span><strong>{health.channelCount}</strong></div> : null}
        {health.recordingState ? <div><span>Recording</span><strong>{health.recordingState}</strong></div> : null}
        {health.diskState ? <div><span>Disk state</span><strong>{health.diskState}</strong></div> : null}
      </div>
    </div>
  );
};

const ProviderHealthCard = ({ device, managedInterfaces }) => {
  const types = managedInterfaces.map((i) => String(i.type || '').toLowerCase());
  const hasTruenas = types.some((t) => t.includes('truenas'));
  const hasPfsense = types.some((t) => t.includes('pfsense'));
  const hasIsapi = types.some((t) => t === 'isapi');
  const hasOnvif = types.some((t) => t === 'onvif');
  const hasSonoff = types.some((t) => t === 'sonoff_diy' || t === 'sonoff_lan');

  const details = device?.details || {};
  const truenasHealth = details.truenasHealth || details.truenas_health;
  const pfsenseHealth = details.pfsenseHealth || details.pfsense_health;
  const nvrHealth = details.nvrHealth || details.nvr_health || details.isapi || details.onvifManaged;
  const sonoffHealth = details.sonoffHealth || details.sonoff_health || details.sonoff?.state;

  if (hasTruenas && truenasHealth) {
    return <TrueNASHealthCard health={truenasHealth} />;
  }
  if (hasPfsense && pfsenseHealth) {
    return <PfSenseHealthCard health={pfsenseHealth} />;
  }
  if ((hasIsapi || hasOnvif) && nvrHealth) {
    return <NvrHealthCard health={nvrHealth} />;
  }
  if (hasSonoff && sonoffHealth) {
    return <SonoffHealthCard health={sonoffHealth} />;
  }

  const lastCheck = managedInterfaces.map((i) => i.lastHealthAt).filter(Boolean).sort().pop();
  return (
    <EmptyState
      title="Awaiting first authenticated health pull"
      text={lastCheck ? `Last check: ${formatDateTime(lastCheck)}. Run Re\u2011test on the managed interface to force a new pull.` : 'No authenticated health data yet. Use Re\u2011test on the managed interface to trigger an immediate health pull.'}
    />
  );
};

const AdminNetworkDeviceDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const networkScope = useNetworkOwnerOrgScope();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [device, setDevice] = useState(null);
  const [latency, setLatency] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [managedInterfaces, setManagedInterfaces] = useState([]);
  const [interfaceDraft, setInterfaceDraft] = useState(null);
  const [savingInterface, setSavingInterface] = useState(false);
  const [testingInterfaceIds, setTestingInterfaceIds] = useState(() => new Set());
  const testingInterfaceIdsRef = useRef(new Set());
  const probeGenerationRef = useRef(0);
  const latestSonoffProbeRef = useRef(null);
  const detailRequestGenerationRef = useRef(0);
  const detailDeviceId = String(id ?? '');
  const detailDeviceIdRef = useRef(detailDeviceId);
  detailDeviceIdRef.current = detailDeviceId;
  const detailRouteKey = [detailDeviceId, networkScope.ownerOrgId, networkScope.selectedInstallationUuid]
    .map((value) => String(value ?? ''))
    .join(':');
  const detailRouteKeyRef = useRef(detailRouteKey);
  detailRouteKeyRef.current = detailRouteKey;
  const [deletingInterfaceId, setDeletingInterfaceId] = useState(null);
  const [interfaceAccountReports, setInterfaceAccountReports] = useState({});
  const [loadingAccountInterfaceId, setLoadingAccountInterfaceId] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [warningActionId, setWarningActionId] = useState(null);
  const [addressingAction, setAddressingAction] = useState('');
  const metadataEntries = toMetadataEntries(device?.details);
  const displayStatus = getDeviceDisplayStatus(device);
  const onboardingMode = searchParams.get('onboarding') === '1';
  const onboardingState = getOnboardingState(device, managedInterfaces);
  const onboardingGuidance = getOnboardingGuidance(device, managedInterfaces);
  const verificationBlocker = getManagedInterfaceVerificationBlocker(device, managedInterfaces, warnings);

  const loadManagedInterfaces = async () => {
    try {
      const response = await networkApi.getManagedInterfaces(id, networkScope.scopeParams);
      const payload = extractData(response);
      return asArray(payload, ['interfaces']).map(normalizeManagedNetworkInterface);
    } catch (err) {
      if (err?.response?.status === 404) {
        return [];
      }
      throw err;
    }
  };

  const loadWarnings = async () => {
    try {
      const response = await networkApi.getWarnings({ ...networkScope.scopeParams, deviceId: id, device_id: id });
      const payload = extractData(response);
      return asArray(payload, ['warnings', 'items', 'rows']).map(normalizeWarningEntry);
    } catch (err) {
      if (err?.response?.status === 404) {
        return [];
      }
      throw err;
    }
  };

  const loadDependencies = async () => {
    try {
      const response = await networkApi.getDependencies({ ...networkScope.scopeParams, deviceId: id, device_id: id });
      const payload = extractData(response);
      return asArray(payload, ['dependencies', 'items', 'rows']).map(normalizeDependencyEntry);
    } catch (err) {
      if (err?.response?.status === 404) {
        return [];
      }
      throw err;
    }
  };

  const loadHistory = async (nextDevice) => {
    const macAddress = pickString(nextDevice?.macAddress);

    if (!macAddress) {
      return [];
    }

    try {
      const response = await networkApi.getDeviceHistory({ ...networkScope.scopeParams, mac_address: macAddress, macAddress });
      const payload = extractData(response);
      return asArray(payload, ['history', 'items', 'rows', 'events']).map(normalizeHistoryEntry);
    } catch (err) {
      if (err?.response?.status === 404) {
        return [];
      }
      throw err;
    }
  };

  const loadDetail = async (isRequestCurrent = () => true) => {
    const requestedDeviceId = String(id ?? '');
    const requestedRouteKey = detailRouteKey;
    if (detailRouteKeyRef.current !== requestedRouteKey) {
      return false;
    }

    const requestGeneration = detailRequestGenerationRef.current + 1;
    detailRequestGenerationRef.current = requestGeneration;
    const isLatestDetailRequest = () => (
      isRequestCurrent()
      && detailRouteKeyRef.current === requestedRouteKey
      && detailRequestGenerationRef.current === requestGeneration
      && detailDeviceIdRef.current === requestedDeviceId
    );
    const commitIfLatest = (commit) => {
      if (!isLatestDetailRequest()) {
        return false;
      }
      commit();
      return true;
    };

    if (networkScope.loading) {
      return false;
    }

    if (!networkScope.ownerOrgId) {
      commitIfLatest(() => setLoading(false));
      commitIfLatest(() => setError(networkScope.error || 'Select an organisation before loading network device detail.'));
      return false;
    }

    try {
      if (!commitIfLatest(() => setError('')) || !commitIfLatest(() => setLoading(true))) {
        return false;
      }
      const [deviceResponse, latencyResponse, managedInterfaceResponse, warningResponse, dependencyResponse] = await Promise.all([
        networkApi.getDevice(id, networkScope.scopeParams),
        networkApi.getLatency(id, networkScope.scopeParams),
        loadManagedInterfaces(),
        loadWarnings(),
        loadDependencies(),
      ]);
      const devicePayload = extractData(deviceResponse);
      const nextDevice = normalizeDevice(devicePayload?.device ?? devicePayload);
      if (!isLatestDetailRequest()) {
        return false;
      }
      const historyResponse = await loadHistory(nextDevice);
      if (!commitIfLatest(() => setDevice(nextDevice))) return false;
      if (!commitIfLatest(() => setNotes(nextDevice.notes || ''))) return false;
      if (!commitIfLatest(() => setLatency(asArray(extractData(latencyResponse), ['items', 'rows', 'measurements', 'latency']).slice(0, 24)))) return false;
      if (!commitIfLatest(() => setManagedInterfaces(managedInterfaceResponse))) return false;
      if (!commitIfLatest(() => setWarnings(warningResponse))) return false;
      if (!commitIfLatest(() => setDependencies(dependencyResponse))) return false;
      if (!commitIfLatest(() => setHistoryEntries(historyResponse))) return false;
      if (!commitIfLatest(() => setInterfaceDraft(null))) return false;
      if (!commitIfLatest(() => setInterfaceAccountReports({}))) return false;
      return true;
    } catch (err) {
      if (!isLatestDetailRequest()) {
        return false;
      }
      console.error('Failed to load device detail:', err);
      commitIfLatest(() => setError(getNetworkErrorMessage(err, 'Failed to load device detail.')));
      return false;
    } finally {
      commitIfLatest(() => setLoading(false));
    }
  };

  useEffect(() => {
    loadDetail();
  }, [id, networkScope.loading, networkScope.ownerOrgId, networkScope.selectedInstallationUuid]);

  const saveDevice = async () => {
    if (!device?.id) {
      return;
    }

    try {
      setSaving(true);
      await networkApi.updateDevice(device.id, {
        is_approved: device.approved,
        is_critical: device.critical,
        notes,
      }, networkScope.scopeParams);
      await loadDetail();
    } catch (err) {
      console.error('Failed to save device:', err);
      setError(getNetworkErrorMessage(err, 'Failed to save device changes.'));
    } finally {
      setSaving(false);
    }
  };

  const inspectInterfaceAccounts = async (interfaceId) => {
    if (!device?.id) {
      return;
    }

    try {
      setLoadingAccountInterfaceId(interfaceId);
      setError('');
      const response = await networkApi.getDeviceInterfaceAccounts(device.id, interfaceId, networkScope.scopeParams);
      setInterfaceAccountReports((current) => ({
        ...current,
        [interfaceId]: normalizeInterfaceAccountReport(extractData(response)),
      }));
    } catch (err) {
      console.error('Failed to inspect interface accounts:', err);
      setError(getNetworkErrorMessage(err, 'Failed to inspect interface accounts.'));
    } finally {
      setLoadingAccountInterfaceId(null);
    }
  };

  const openNewManagedInterface = () => {
    setInterfaceDraft(getSuggestedManagedInterfaceDraft(device));
  };

  const openEditManagedInterface = (entry) => {
    setInterfaceDraft(createManagedInterfaceDraft({
      id: entry.id,
      type: entry.type,
      label: entry.label,
      baseUrl: entry.baseUrl,
      deviceId: entry.deviceId || '',
      tlsVerify: entry.tlsVerify,
      enabled: entry.enabled,
      notes: entry.notes,
    }));
  };

  const updateInterfaceDraft = (field, value) => {
    setInterfaceDraft((current) => ({
      ...(current || createManagedInterfaceDraft()),
      [field]: value,
    }));
  };

  const changeInterfaceType = (newType) => {
    const currentUrl = interfaceDraft?.baseUrl || '';
    const oldSuggestedUrl = getSuggestedManagedInterfaceBaseUrl(device, interfaceDraft?.type || 'onvif');
    const shouldUpdateUrl = !currentUrl || currentUrl === oldSuggestedUrl;
    setInterfaceDraft((current) => ({
      ...(current || createManagedInterfaceDraft()),
      type: newType,
      ...(shouldUpdateUrl ? { baseUrl: getSuggestedManagedInterfaceBaseUrl(device, newType) } : {}),
    }));
  };

  const saveManagedInterface = async () => {
    if (!device?.id || !interfaceDraft) {
      return;
    }

    const isSonoff = interfaceDraft.type === 'sonoff_diy' || interfaceDraft.type === 'sonoff_lan';

    const payload = {
      type: interfaceDraft.type,
      label: interfaceDraft.label,
      base_url: interfaceDraft.baseUrl,
      tls_verify: interfaceDraft.tlsVerify,
      enabled: interfaceDraft.enabled,
      notes: interfaceDraft.notes,
    };

    if (isSonoff && interfaceDraft.deviceId.trim()) {
      payload.device_id = interfaceDraft.deviceId.trim();
    }

    if (!isSonoff && interfaceDraft.username.trim()) {
      payload.username = interfaceDraft.username.trim();
    }
    if (!isSonoff && interfaceDraft.password.trim()) {
      payload.password = interfaceDraft.password;
    }
    if (interfaceDraft.apiKey.trim()) {
      payload.api_key = interfaceDraft.apiKey.trim();
    }

    try {
      setSavingInterface(true);
      setError('');
      if (interfaceDraft.id) {
        await networkApi.updateManagedInterface(device.id, interfaceDraft.id, payload, networkScope.scopeParams);
      } else {
        await networkApi.createManagedInterface(device.id, payload, networkScope.scopeParams);
      }
      await loadDetail();
      setInterfaceDraft(null);
    } catch (err) {
      console.error('Failed to save managed interface:', err);
      setError(getNetworkErrorMessage(err, 'Failed to save managed interface.'));
    } finally {
      setSavingInterface(false);
    }
  };

  const testManagedInterface = async (interfaceId) => {
    if (!device?.id || !interfaceId || testingInterfaceIdsRef.current.has(interfaceId)) {
      return;
    }

    const deviceId = device.id;
    const probeRouteKey = detailRouteKey;
    const targetInterface = managedInterfaces.find((entry) => entry.id === interfaceId);
    const isSonoff = targetInterface?.type === 'sonoff_diy' || targetInterface?.type === 'sonoff_lan';
    const probeGeneration = probeGenerationRef.current + 1;
    probeGenerationRef.current = probeGeneration;
    const isLatestProbeRequest = () => (
      detailRouteKeyRef.current === probeRouteKey
      && detailDeviceIdRef.current === String(deviceId)
      && probeGenerationRef.current === probeGeneration
    );
    if (isSonoff) {
      latestSonoffProbeRef.current = {
        deviceId,
        generation: probeGeneration,
        state: null,
      };
    }

    testingInterfaceIdsRef.current = new Set(testingInterfaceIdsRef.current).add(interfaceId);
    setTestingInterfaceIds(new Set(testingInterfaceIdsRef.current));

    try {
      if (!isLatestProbeRequest()) {
        return;
      }
      setError('');
      const response = await networkApi.testManagedInterface(deviceId, interfaceId, networkScope.scopeParams);
      if (!isLatestProbeRequest()) {
        return;
      }
      const probeResult = extractData(response);
      const canonicalState = isSonoff && response?.data?.success === true
        ? getCanonicalSonoffProbeState(probeResult)
        : null;
      if (
        canonicalState
        && isLatestProbeRequest()
        && latestSonoffProbeRef.current?.deviceId === deviceId
        && latestSonoffProbeRef.current?.generation === probeGeneration
      ) {
        latestSonoffProbeRef.current = {
          deviceId,
          generation: probeGeneration,
          state: canonicalState,
        };
      }
      if (!isLatestProbeRequest()) {
        return;
      }
      await loadDetail(isLatestProbeRequest);
      if (!isLatestProbeRequest()) {
        return;
      }
      const latestSonoffProbe = latestSonoffProbeRef.current;
      if (
        isSonoff
        && latestSonoffProbe?.deviceId === deviceId
        && latestSonoffProbe.generation === probeGeneration
        && latestSonoffProbe.state
      ) {
        setDevice((current) => (
          String(current?.id) === String(deviceId)
            ? {
              ...current,
              details: {
                ...(current.details || {}),
                sonoffHealth: latestSonoffProbe.state,
              },
            }
            : current
        ));
      }
    } catch (err) {
      if (!isLatestProbeRequest()) {
        return;
      }
      console.error('Failed to test managed interface:', err);
      setError(getNetworkErrorMessage(err, 'Failed to test managed interface.'));
    } finally {
      const nextTestingInterfaceIds = new Set(testingInterfaceIdsRef.current);
      nextTestingInterfaceIds.delete(interfaceId);
      testingInterfaceIdsRef.current = nextTestingInterfaceIds;
      setTestingInterfaceIds(new Set(nextTestingInterfaceIds));
    }
  };

  const deleteManagedInterface = async (interfaceId) => {
    if (!device?.id || !interfaceId) {
      return;
    }

    try {
      setDeletingInterfaceId(interfaceId);
      setError('');
      await networkApi.deleteManagedInterface(device.id, interfaceId, networkScope.scopeParams);
      await loadDetail();
    } catch (err) {
      console.error('Failed to delete managed interface:', err);
      setError(getNetworkErrorMessage(err, 'Failed to delete managed interface.'));
    } finally {
      setDeletingInterfaceId(null);
    }
  };

  const acknowledgeWarning = async (warningId) => {
    try {
      setWarningActionId(warningId);
      setError('');
      await networkApi.acknowledgeWarning(warningId, {}, networkScope.scopeParams);
      await loadDetail();
    } catch (err) {
      console.error('Failed to acknowledge warning:', err);
      setError(getNetworkErrorMessage(err, 'Failed to acknowledge warning.'));
    } finally {
      setWarningActionId(null);
    }
  };

  const resolveWarning = async (warningId) => {
    try {
      setWarningActionId(warningId);
      setError('');
      await networkApi.resolveWarning(warningId, {}, networkScope.scopeParams);
      await loadDetail();
    } catch (err) {
      console.error('Failed to resolve warning:', err);
      setError(getNetworkErrorMessage(err, 'Failed to resolve warning.'));
    } finally {
      setWarningActionId(null);
    }
  };

  const updateAddressingMode = async (mode, applyVia) => {
    if (!device?.id) {
      return;
    }

    try {
      setAddressingAction(mode);
      setError('');
      await networkApi.updateAddressingPolicy(device.id, {
        requested_mode: mode,
        requestedMode: mode,
        mode,
        apply_via: applyVia,
        applyVia,
      }, networkScope.scopeParams);
      await loadDetail();
    } catch (err) {
      console.error('Failed to update addressing policy:', err);
      setError(getNetworkErrorMessage(err, 'Failed to update addressing policy.'));
    } finally {
      setAddressingAction('');
    }
  };

  return (
    <Layout>
      <div className="network-page">
        <SectionHeader title="Device Detail" subtitle="Identity, onboarding, addressing, managed interfaces, admin controls, warnings, dependency facts, and evidence for one device." actions={<Link to="/app/admin/network/devices" className="network-secondary-link">Back to device registry</Link>} />
        {loading ? <LoadingPanel title="Loading device detail..." /> : null}
        {error ? <div className="network-error-banner">{error}</div> : null}
        {!loading && !device ? <EmptyState title="Device not found" text="The backend did not return a device for this identifier." /> : null}
        {!loading && device ? (
          <div className="network-panel-grid two-up">
            <section className="network-panel">
              <div className="network-panel-header">
                <h2>Identity</h2>
                <span className={`network-pill severity-${getSeverityClass(displayStatus)}`}>{displayStatus}</span>
              </div>
              <div className="network-detail-block">
                <span>Device</span>
                <p><strong>{device.hostname || device.ipAddress || 'Unknown device'}</strong></p>
              </div>
              <div className="network-detail-grid">
                <div><span>IP</span><strong>{device.ipAddress || 'Unknown'}</strong></div>
                <div><span>MAC</span><strong>{device.macAddress || 'Unknown'}</strong></div>
                <div><span>Type</span><strong>{device.deviceType}</strong></div>
                <div><span>Manufacturer</span><strong>{device.manufacturer || 'Unknown'}</strong></div>
                <div><span>Network reachability</span><strong>{device.status || 'Unknown'}</strong></div>
                <div><span>Managed health</span><strong>{device.healthState || 'Not configured'}</strong></div>
                <div><span>Management state</span><strong>{device.managementState || 'unmanaged'}</strong></div>
                <div><span>First seen</span><strong>{formatDateTime(device.firstSeen)}</strong></div>
                <div><span>Last seen</span><strong>{formatDateTime(device.lastSeen)}</strong></div>
                <div><span>RTT baseline</span><strong>{formatLatency(Number(device.baseline))}</strong></div>
                <div><span>Approved</span><strong>{formatBoolean(device.approved)}</strong></div>
                <div><span>Critical</span><strong>{formatBoolean(device.critical)}</strong></div>
              </div>

              <div className="network-detail-block">
                <span>Identification Data</span>
                {metadataEntries.length === 0 ? <p>No identification metadata returned.</p> : null}
                {metadataEntries.length > 0 ? (
                  <div className="network-metadata-list">
                    {metadataEntries.map((entry) => (
                      <div key={`${entry.key}-${entry.value}`} className="network-metadata-item">
                        <span>{entry.key}</span>
                        <strong>{entry.value}</strong>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </section>

            <section className="network-panel">
              <div className="network-panel-header">
                <h2>Onboarding</h2>
                <span className={`network-pill severity-${getSeverityClass(onboardingState === 'degraded' ? 'degraded' : 'info')}`}>{formatLabel(onboardingState)}</span>
              </div>
              {onboardingMode ? <div className="network-callout">Onboarding mode opened from the device list. Review trust, addressing, and interface setup for this device.</div> : null}
              <div className="network-detail-grid">
                <div><span>Current state</span><strong>{formatLabel(onboardingState)}</strong></div>
                <div><span>Health presentation</span><strong>{displayStatus}</strong></div>
                <div><span>Verification result</span><strong>{device.verificationResult || 'Not verified yet'}</strong></div>
                <div><span>Management state</span><strong>{device.managementState || 'unmanaged'}</strong></div>
                <div><span>Managed interfaces</span><strong>{managedInterfaces.length}</strong></div>
                <div><span>Device type</span><strong>{device.deviceType || 'unknown'}</strong></div>
                <div><span>Identity binding</span><strong>{device.identityBinding || 'Not set'}</strong></div>
                <div><span>Blocking warnings</span><strong>{warnings.filter((warning) => warning.blocking && !warning.resolved).length}</strong></div>
              </div>
              <p className="network-supporting-text">{onboardingGuidance}</p>
              <button className="network-primary-button" type="button" disabled>
                Mark as Verified
              </button>
              <p className="network-supporting-text">{verificationBlocker || 'Verification is available once the backend exposes the verification action.'}</p>
            </section>

            <section className="network-panel network-panel-span-full">
              <div className="network-panel-header">
                <h2>Addressing</h2>
                <span className="network-summary-helper">Current addressing mode, recommendation, and change actions</span>
              </div>
              <div className="network-detail-grid">
                <div><span>Current IP</span><strong>{device.ipAddress || 'Unknown'}</strong></div>
                <div><span>Current MAC</span><strong>{device.macAddress || 'Unknown'}</strong></div>
                <div><span>Current mode</span><strong>{device.addressingMode || 'Not reviewed'}</strong></div>
                <div><span>Recommended mode</span><strong>{device.recommendedAddressingMode || 'No recommendation'}</strong></div>
                <div><span>Apply via</span><strong>{device.addressingApplyVia || 'guide_only'}</strong></div>
                <div><span>Preferred IP</span><strong>{device.preferredIpAddress || 'Not set'}</strong></div>
                <div><span>Stable address required</span><strong>{formatBoolean(device.stableAddressRequired)}</strong></div>
                <div><span>Allow floating IP</span><strong>{formatBoolean(device.allowFloatingIp)}</strong></div>
              </div>
              <div className="network-inline-actions">
                <button className="network-secondary-button" type="button" disabled={Boolean(addressingAction)} onClick={() => updateAddressingMode('dynamic', 'guide_only')}>{addressingAction === 'dynamic' ? 'Saving...' : 'Set Dynamic'}</button>
                <button className="network-secondary-button" type="button" disabled={Boolean(addressingAction)} onClick={() => updateAddressingMode('dhcp_reservation', 'pfsense')}>{addressingAction === 'dhcp_reservation' ? 'Saving...' : 'Set Stable via pfSense'}</button>
                <button className="network-secondary-button" type="button" disabled={Boolean(addressingAction)} onClick={() => updateAddressingMode('manual_static', 'manual_device')}>{addressingAction === 'manual_static' ? 'Saving...' : 'Set Manual Static'}</button>
                <button className="network-secondary-button" type="button" disabled>Open Help</button>
              </div>
              <p className="network-supporting-text">{device.stableAddressRequired ? 'Stable addressing is recommended because this device role is infrastructure-critical and dependency links become fragile when the address drifts.' : 'The backend write path for addressing policy is still limited, so this page currently exposes the recommendation and actions in a read-first state.'}</p>
            </section>

            <section className="network-panel network-panel-span-full" style={{ order: -2 }}>
              <div className="network-panel-header">
                <h2>Managed Interfaces</h2>
                {!interfaceDraft ? (
                  <button className="network-primary-button" onClick={openNewManagedInterface} type="button">
                    + Add Interface
                  </button>
                ) : null}
              </div>

              {managedInterfaces.length === 0 ? (
                <EmptyState title="No managed interfaces" text="Use Add Interface below to start onboarding for ONVIF, NVR, TrueNAS, pfSense, or SONOFF." />
              ) : (
                <div className="network-managed-interface-list" data-testid="network-device-interfaces">
                  {managedInterfaces.map((entry) => {
                    const accountReport = interfaceAccountReports[entry.id];

                    return (
                    <div key={entry.id || `${entry.interfaceName}-${entry.ipAddress}-${entry.macAddress}`} className="network-managed-interface-card" data-testid={`network-interface-card-${entry.id}`}>
                      <div className="network-managed-interface-header">
                        <div>
                          <strong>{entry.label || 'Managed interface'}</strong>
                          <span>{entry.type || 'interface'}</span>
                        </div>
                        <span className={`network-pill severity-${getSeverityClass(entry.healthState || entry.lastTestResult || 'info')}`}>
                          {entry.healthState || entry.lastTestResult || entry.credentialState || 'unknown'}
                        </span>
                      </div>
                      <div className="network-detail-grid">
                        <div><span>Base URL</span><strong>{entry.baseUrl || 'Not set'}</strong></div>
                        <div><span>Credential state</span><strong>{entry.credentialState || 'Unknown'}</strong></div>
                        <div><span>Enabled</span><strong>{formatBoolean(entry.enabled)}</strong></div>
                        <div><span>TLS verify</span><strong>{formatBoolean(entry.tlsVerify)}</strong></div>
                        <div><span>Last test</span><strong>{formatDateTime(entry.lastTestAt)}</strong></div>
                        <div><span>Last health</span><strong>{formatDateTime(entry.lastHealthAt)}</strong></div>
                      </div>

                      <p className="network-supporting-text">Secrets are write-only. Passwords, tokens, and API keys are never shown again after save.</p>

                      {entry.notes ? <p>{entry.notes}</p> : null}

                      <div className="network-inline-actions">
                        <button
                          className="network-secondary-button"
                          disabled={savingInterface || deletingInterfaceId === entry.id}
                          onClick={() => openEditManagedInterface(entry)}
                          type="button"
                        >
                          Edit
                        </button>
                        <button
                          className="network-secondary-button"
                          disabled={!entry.id || testingInterfaceIds.has(entry.id) || savingInterface || deletingInterfaceId === entry.id}
                          onClick={() => testManagedInterface(entry.id)}
                          type="button"
                        >
                          {testingInterfaceIds.has(entry.id) ? 'Testing...' : 'Re-test'}
                        </button>
                        <button
                          className="network-secondary-button"
                          disabled={!entry.id || loadingAccountInterfaceId === entry.id || savingInterface || deletingInterfaceId === entry.id}
                          onClick={() => inspectInterfaceAccounts(entry.id)}
                          type="button"
                        >
                          {loadingAccountInterfaceId === entry.id ? 'Checking...' : 'Check ONVIF accounts'}
                        </button>
                        <button
                          className="network-secondary-button"
                          disabled={!entry.id || deletingInterfaceId === entry.id || savingInterface}
                          onClick={() => deleteManagedInterface(entry.id)}
                          type="button"
                        >
                          {deletingInterfaceId === entry.id ? 'Deleting...' : entry.enabled ? 'Disable/Delete' : 'Delete'}
                        </button>
                      </div>

                      {accountReport ? (
                        <div className="network-detail-block">
                          <span>Account integrity</span>
                          <div className="network-inline-actions">
                            <span className={`network-pill severity-${getSeverityClass(getInterfaceIntegritySeverity(accountReport))}`}>
                              {accountReport.integrityStatus}
                            </span>
                            {accountReport.warningCode ? <span>{accountReport.warningCode}</span> : null}
                          </div>
                          <p>Checked: {formatDateTime(accountReport.queriedAt)}</p>
                          <p>Anonymous access: {formatBoolean(accountReport.anonymousAccessEnabled)}</p>
                          <p>Live users: {accountReport.users.length > 0 ? accountReport.users.map((userEntry) => `${userEntry.username} (${userEntry.userLevel || 'unknown'})`).join(', ') : 'None returned'}</p>
                          <p>Approved users: {accountReport.approvedAccounts.length > 0 ? accountReport.approvedAccounts.join(', ') : 'None recorded'}</p>
                          <p>Unknown users: {accountReport.unknownAccounts.length > 0 ? accountReport.unknownAccounts.join(', ') : 'None'}</p>
                          {accountReport.liveCheckError ? <p>{accountReport.liveCheckError}</p> : null}
                        </div>
                      ) : null}
                    </div>
                  )})}
                </div>
              )}

            </section>

            {interfaceDraft ? (
              <section className="network-panel network-panel-span-full network-add-interface-panel" style={{ order: -1 }} data-testid="managed-interface-form">
                <div className="network-panel-header">
                  <div>
                    <h2>{interfaceDraft.id ? 'Edit Interface' : 'Add Interface'}</h2>
                    <p className="network-supporting-text">Select the interface type then fill in the connection details.</p>
                  </div>
                  <button className="network-secondary-button" disabled={savingInterface} onClick={() => setInterfaceDraft(null)} type="button">Cancel</button>
                </div>

                <div className="network-add-interface-type-row">
                  <label className="network-add-interface-type-label" htmlFor="add-interface-type">Interface type</label>
                  <select
                    id="add-interface-type"
                    value={interfaceDraft.type}
                    onChange={(event) => changeInterfaceType(event.target.value)}
                    className="network-select network-add-interface-type-select"
                    aria-label="Interface type"
                  >
                    {MANAGED_INTERFACE_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} — {option.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="network-add-interface-fields">
                  {(interfaceDraft.type === 'sonoff_diy' || interfaceDraft.type === 'sonoff_lan') && (
                    <div className="network-add-interface-guidance">
                      {interfaceDraft.type === 'sonoff_diy' ? (
                        <p>DIY / LAN mode — no credentials required.  Device was identified via mDNS <code>_ewelink._tcp</code>.  Saving will trigger an immediate probe via <code>POST /zeroconf/info</code>.</p>
                      ) : (
                        <p>eWeLink LAN mode — API key required.  The device must have been paired with the eWeLink app first.  Obtain the key from the eWeLink app or connect to the device setup AP at <code>http://10.10.7.1/device</code>.</p>
                      )}
                    </div>
                  )}
                  {(interfaceDraft.type === 'sonoff_diy' || interfaceDraft.type === 'sonoff_lan') && (
                    <label className="network-notes-field">
                      <span>SONOFF Device ID</span>
                      <input
                        value={interfaceDraft.deviceId}
                        readOnly
                        aria-readonly="true"
                        className="network-input"
                        placeholder="e.g. 1000806ace"
                        maxLength={12}
                      />
                    </label>
                  )}
                  <label className="network-notes-field">
                    <span>Label</span>
                    <input value={interfaceDraft.label} onChange={(event) => updateInterfaceDraft('label', event.target.value)} className="network-input" placeholder="e.g. Primary NVR, pfSense Main" />
                  </label>
                  <label className="network-notes-field">
                    <span>Base URL</span>
                    <input value={interfaceDraft.baseUrl} onChange={(event) => updateInterfaceDraft('baseUrl', event.target.value)} className="network-input" placeholder="https://192.168.1.1" />
                  </label>
                  {interfaceDraft.type !== 'sonoff_diy' && interfaceDraft.type !== 'sonoff_lan' && (
                    <label className="network-notes-field">
                      <span>Username</span>
                      <input value={interfaceDraft.username} onChange={(event) => updateInterfaceDraft('username', event.target.value)} className="network-input" placeholder="admin" />
                    </label>
                  )}
                  {interfaceDraft.type !== 'sonoff_diy' && interfaceDraft.type !== 'sonoff_lan' && (
                    <label className="network-notes-field">
                      <span>Password</span>
                      <input type="password" value={interfaceDraft.password} onChange={(event) => updateInterfaceDraft('password', event.target.value)} className="network-input" placeholder={interfaceDraft.id ? 'Leave blank to keep current' : 'Password'} />
                    </label>
                  )}
                  {(interfaceDraft.type === 'sonoff_lan' || (interfaceDraft.type !== 'sonoff_diy' && interfaceDraft.type !== 'sonoff_lan')) && (
                    <label className="network-notes-field">
                      <span>{interfaceDraft.type === 'sonoff_lan' ? 'eWeLink API key (devicekey)' : 'API key'}</span>
                      <input
                        type={interfaceDraft.type === 'sonoff_lan' ? 'password' : 'text'}
                        value={interfaceDraft.apiKey}
                        onChange={(event) => updateInterfaceDraft('apiKey', event.target.value)}
                        className="network-input"
                        placeholder={interfaceDraft.id && interfaceDraft.type === 'sonoff_lan' ? 'Leave blank to keep current key' : interfaceDraft.type === 'sonoff_lan' ? 'Paste eWeLink API key' : (interfaceDraft.id ? 'Optional replacement key' : 'Optional API key')}
                      />
                    </label>
                  )}
                </div>

                <div className="network-toggle-row">
                  {interfaceDraft.type !== 'sonoff_diy' && interfaceDraft.type !== 'sonoff_lan' && (
                    <label>
                      <input type="checkbox" checked={interfaceDraft.tlsVerify} onChange={(event) => updateInterfaceDraft('tlsVerify', event.target.checked)} />
                      Verify TLS certificate
                    </label>
                  )}
                  <label>
                    <input type="checkbox" checked={interfaceDraft.enabled} onChange={(event) => updateInterfaceDraft('enabled', event.target.checked)} />
                    Interface enabled
                  </label>
                </div>

                <label className="network-notes-field">
                  <span>Notes</span>
                  <textarea value={interfaceDraft.notes} onChange={(event) => updateInterfaceDraft('notes', event.target.value)} rows={3} placeholder="Self-signed cert, ownership, or device-specific notes" />
                </label>

                <div className="network-inline-actions">
                  <button className="network-primary-button" disabled={savingInterface} onClick={saveManagedInterface} type="button">
                    {savingInterface ? 'Saving...' : interfaceDraft.id ? 'Save Interface' : 'Create Interface'}
                  </button>
                  <button className="network-secondary-button" disabled={savingInterface} onClick={() => setInterfaceDraft(null)} type="button">
                    Cancel
                  </button>
                </div>
              </section>
            ) : null}

            {managedInterfaces.length > 0 ? (
              <section className="network-panel network-panel-span-full" data-testid="provider-monitor-panel">
                <div className="network-panel-header">
                  <h2>Monitor</h2>
                  <span className="network-summary-helper">Authenticated health from managed interface</span>
                </div>
                <ProviderHealthCard device={device} managedInterfaces={managedInterfaces} />
              </section>
            ) : null}

            <section className="network-panel network-panel-span-full">
              <div className="network-panel-header">
                <h2>Onboarding Warnings</h2>
              </div>
              {warnings.length === 0 ? (
                <EmptyState title="No onboarding warnings" text="No device-scoped warning records were returned for this device." />
              ) : (
                <>
                  <div className="network-managed-interface-list">
                    {warnings.map((warning) => (
                      <div key={warning.id || `${warning.code}-${warning.title}`} className="network-managed-interface-card">
                        <div className="network-managed-interface-header">
                          <div>
                            <strong>{warning.title}</strong>
                            <span>{warning.code || warning.state}</span>
                          </div>
                          <span className={`network-pill severity-${getSeverityClass(warning.severity)}`}>{warning.severity}</span>
                        </div>
                        <p>{warning.meaning || 'No warning explanation returned.'}</p>
                        <p><strong>Option A:</strong> {warning.optionASummary || 'No preferred action summary returned.'}</p>
                        <p><strong>Lifecycle:</strong> {formatLabel(warning.state)}</p>
                        <div className="network-inline-actions">
                          <Link className="network-secondary-link" to={warning.id ? `/app/admin/network/warnings/${warning.id}` : '/app/admin/network/warnings'}>Review</Link>
                          <button className="network-secondary-button" disabled={warningActionId === warning.id || warning.resolved} onClick={() => acknowledgeWarning(warning.id)} type="button">
                            {warningActionId === warning.id ? 'Working...' : 'Acknowledge'}
                          </button>
                          <button className="network-secondary-button" disabled={warningActionId === warning.id || warning.resolved} onClick={() => resolveWarning(warning.id)} type="button">
                            {warningActionId === warning.id ? 'Working...' : 'Resolve'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>

            <section className="network-panel network-panel-span-full">
              <div className="network-panel-header">
                <h2>Admin Controls</h2>
              </div>
              <div className="network-toggle-row">
                <label>
                  <input type="checkbox" checked={device.approved} onChange={(event) => setDevice((current) => ({ ...current, approved: event.target.checked }))} />
                  Approved device
                </label>
                <label>
                  <input type="checkbox" checked={device.critical} onChange={(event) => setDevice((current) => ({ ...current, critical: event.target.checked }))} />
                  Critical device
                </label>
              </div>

              <label className="network-notes-field">
                <span>Notes</span>
                <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={5} placeholder="Operational notes, ownership, or maintenance context" />
              </label>

              <button className="network-primary-button" disabled={saving} onClick={saveDevice}>
                {saving ? 'Saving...' : 'Save device settings'}
              </button>
            </section>

            <section className="network-panel">
              <div className="network-panel-header">
                <h2>Dependency Facts</h2>
              </div>
              {dependencies.length === 0 ? (
                <EmptyState title="No dependency facts" text="No configured, observed, or inferred dependency facts were returned for this device." />
              ) : (
                <div className="network-managed-interface-list">
                  {dependencies.map((entry) => (
                    <div key={entry.id || entry.label} className="network-managed-interface-card">
                      <div className="network-managed-interface-header">
                        <div>
                          <strong>{entry.label}</strong>
                          <span>{entry.status}</span>
                        </div>
                      </div>
                      <div className="network-detail-grid">
                        <div><span>Configured</span><strong>{entry.configured || 'Unknown'}</strong></div>
                        <div><span>Observed</span><strong>{entry.observed || 'Unknown'}</strong></div>
                        <div><span>Inferred</span><strong>{entry.inferred || 'Unknown'}</strong></div>
                        <div><span>Unknown</span><strong>{entry.unknown || 'None'}</strong></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="network-panel">
              <div className="network-panel-header">
                <h2>History &amp; Evidence</h2>
              </div>
              {historyEntries.length === 0 && latency.length === 0 ? (
                <EmptyState title="No evidence yet" text="The network gateway has not returned device history or latency evidence for this device yet." />
              ) : (
                <>
                  {historyEntries.length > 0 ? (
                    <div className="network-activity-list">
                      {historyEntries.map((entry) => (
                        <div key={entry.id || `${entry.eventType}-${entry.detectedAt}`} className="network-activity-item">
                          <div>
                            <strong>{formatLabel(entry.eventType)}</strong>
                            <p>{entry.summary || 'No summary returned.'}</p>
                            {entry.previousValue || entry.nextValue ? (
                              <small>{[entry.previousValue || 'Unknown', entry.nextValue || 'Unknown'].join(' -> ')}</small>
                            ) : null}
                          </div>
                          <div className="network-activity-meta">
                            <span>{formatDateTime(entry.detectedAt)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {latency.length > 0 ? (
                  <div className="network-table-wrap">
                    <table className="network-table compact">
                      <thead>
                        <tr>
                          <th>Measured</th>
                          <th>Check</th>
                          <th>Min</th>
                          <th>Avg</th>
                          <th>Max</th>
                          <th>Loss</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latency.map((entry, index) => (
                          <tr key={entry.id || `${entry.measured_at || entry.measuredAt}-${index}`}>
                            <td>{formatDateTime(entry.measuredAt || entry.measured_at)}</td>
                            <td>{entry.checkType || entry.check_type || 'icmp'}</td>
                            <td>{formatLatency(Number(entry.rttMinMs ?? entry.rtt_min_ms))}</td>
                            <td>{formatLatency(Number(entry.rttAvgMs ?? entry.rtt_avg_ms))}</td>
                            <td>{formatLatency(Number(entry.rttMaxMs ?? entry.rtt_max_ms))}</td>
                            <td>{Number.isFinite(Number(entry.packetLossPct ?? entry.packet_loss_pct)) ? `${Number(entry.packetLossPct ?? entry.packet_loss_pct).toFixed(1)}%` : 'n/a'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  ) : null}
                  <div className="network-detail-block">
                    <span>Raw metadata</span>
                    <pre className="network-json-block">{JSON.stringify(device.details || {}, null, 2)}</pre>
                  </div>
                </>
              )}
            </section>
          </div>
        ) : null}
      </div>
    </Layout>
  );
};

const AdminNetworkAnomalies = () => {
  const networkScope = useNetworkOwnerOrgScope();
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [severity, setSeverity] = useState('all');
  const [resolved, setResolved] = useState('open');
  const [workingId, setWorkingId] = useState(null);

  const loadAnomalies = async () => {
    if (networkScope.loading) {
      return;
    }

    try {
      setError('');
      setLoading(true);
      const response = await networkApi.getAnomalies({
        ...networkScope.scopeParams,
        severity: severity !== 'all' ? severity : undefined,
        resolved: resolved === 'all' ? undefined : resolved === 'resolved',
      });
      setAnomalies(asArray(extractData(response), ['items', 'rows', 'anomalies']).map(normalizeAnomaly));
    } catch (err) {
      console.error('Failed to load anomalies:', err);
      setError(getNetworkErrorMessage(err, 'Failed to load anomalies.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnomalies();
  }, [severity, resolved, networkScope.loading, networkScope.ownerOrgId, networkScope.selectedInstallationUuid]);

  useNetworkRefresh(() => {
    loadAnomalies();
  });

  const resolveAnomaly = async (anomalyId) => {
    try {
      setWorkingId(anomalyId);
      await networkApi.resolveAnomaly(anomalyId, {}, networkScope.scopeParams);
      await loadAnomalies();
    } catch (err) {
      console.error('Failed to resolve anomaly:', err);
      setError(getNetworkErrorMessage(err, 'Failed to resolve anomaly.'));
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <Layout>
      <div className="network-page">
        <SectionHeader title="Anomaly Log" subtitle="Security and health anomalies returned through the Mycelium network gateway." />

        <div className="network-toolbar">
          <select value={severity} onChange={(event) => setSeverity(event.target.value)} className="network-select">
            <option value="all">All severities</option>
            <option value="INFO">Info</option>
            <option value="WARNING">Warning</option>
            <option value="HIGH">High</option>
            <option value="CRITICAL">Critical</option>
          </select>
          <select value={resolved} onChange={(event) => setResolved(event.target.value)} className="network-select">
            <option value="open">Open only</option>
            <option value="resolved">Resolved only</option>
            <option value="all">All anomalies</option>
          </select>
        </div>

        {error ? <div className="network-error-banner">{error}</div> : null}
        {loading ? <LoadingPanel title="Loading anomalies..." /> : null}
        {!loading && anomalies.length === 0 ? <EmptyState title="No anomaly records" text="The selected filters returned no network anomalies." /> : null}
        {!loading && anomalies.length > 0 ? (
          <div className="network-activity-list">
            {anomalies.map((anomaly) => (
              <div key={anomaly.id || `${anomaly.type}-${anomaly.detectedAt}`} className={`network-activity-item severity-${getSeverityClass(anomaly.severity)}`}>
                <div>
                  <Link to={`/app/admin/network/anomalies/${anomaly.id}`} className="network-row-link">
                    {anomaly.title}
                  </Link>
                  <p>{anomaly.detail || anomaly.type}</p>
                  <small>{anomaly.sourceIp || anomaly.sourceMac ? `${anomaly.sourceIp || ''} ${anomaly.sourceMac || ''}`.trim() : 'No source metadata'}</small>
                </div>
                <div className="network-activity-meta">
                  <span className={`network-pill severity-${getSeverityClass(anomaly.severity)}`}>{anomaly.severity}</span>
                  <span>{formatDateTime(anomaly.detectedAt)}</span>
                  {!anomaly.resolved ? (
                    <button className="network-secondary-button" disabled={workingId === anomaly.id} onClick={() => resolveAnomaly(anomaly.id)}>
                      {workingId === anomaly.id ? 'Resolving...' : 'Resolve'}
                    </button>
                  ) : (
                    <span className="network-resolved-text">Resolved {formatDateTime(anomaly.resolvedAt)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Layout>
  );
};

const AdminNetworkAnomalyDetail = () => {
  const { id } = useParams();
  const networkScope = useNetworkOwnerOrgScope();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anomaly, setAnomaly] = useState(null);
  const [resolving, setResolving] = useState(false);

  const loadAnomaly = async () => {
    if (networkScope.loading) {
      return;
    }

    try {
      setError('');
      setLoading(true);
      const response = await networkApi.getAnomaly(id, networkScope.scopeParams);
      setAnomaly(normalizeAnomaly(extractData(response)));
    } catch (err) {
      console.error('Failed to load anomaly detail:', err);
      setError(getNetworkErrorMessage(err, 'Failed to load anomaly detail.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnomaly();
  }, [id, networkScope.loading, networkScope.ownerOrgId, networkScope.selectedInstallationUuid]);

  const resolveNow = async () => {
    try {
      setResolving(true);
      await networkApi.resolveAnomaly(id, {}, networkScope.scopeParams);
      await loadAnomaly();
    } catch (err) {
      console.error('Failed to resolve anomaly:', err);
      setError(getNetworkErrorMessage(err, 'Failed to resolve anomaly.'));
    } finally {
      setResolving(false);
    }
  };

  return (
    <Layout>
      <div className="network-page">
        <SectionHeader title="Anomaly Detail" subtitle="Full anomaly context and resolution state." actions={<Link to="/app/admin/network/anomalies" className="network-secondary-link">Back to anomaly log</Link>} />
        {loading ? <LoadingPanel title="Loading anomaly detail..." /> : null}
        {error ? <div className="network-error-banner">{error}</div> : null}
        {!loading && !anomaly ? <EmptyState title="Anomaly not found" text="The backend did not return anomaly data for this identifier." /> : null}
        {!loading && anomaly ? (
          <section className={`network-panel severity-${getSeverityClass(anomaly.severity)}`}>
            <div className="network-panel-header">
              <h2>{anomaly.title}</h2>
              <span className={`network-pill severity-${getSeverityClass(anomaly.severity)}`}>{anomaly.severity}</span>
            </div>
            <div className="network-detail-grid">
              <div><span>Type</span><strong>{anomaly.type}</strong></div>
              <div><span>Detected</span><strong>{formatDateTime(anomaly.detectedAt)}</strong></div>
              <div><span>Source IP</span><strong>{anomaly.sourceIp || 'Unknown'}</strong></div>
              <div><span>Source MAC</span><strong>{anomaly.sourceMac || 'Unknown'}</strong></div>
              <div><span>Status</span><strong>{anomaly.resolved ? `Resolved ${formatDateTime(anomaly.resolvedAt)}` : 'Open'}</strong></div>
            </div>
            <div className="network-detail-block">
              <span>Detail</span>
              <p>{anomaly.detail || 'No anomaly detail was returned by the backend.'}</p>
            </div>
            {!anomaly.resolved ? (
              <button className="network-primary-button" disabled={resolving} onClick={resolveNow}>
                {resolving ? 'Resolving...' : 'Mark anomaly resolved'}
              </button>
            ) : null}
          </section>
        ) : null}
      </div>
    </Layout>
  );
};

const AdminNetworkDependencies = () => (
  <AdminNetworkPlaceholderView
    title="Network Dependencies"
    subtitle="Track dependency relationships between network services, monitoring systems, and managed devices."
    message="Dependency mapping is not wired into this frontend yet. Use the network overview and health screens while the dedicated dependency view is completed."
    primaryLink={{ to: '/app/admin/network/health', label: 'Open health view' }}
    secondaryLink={{ to: '/app/admin/network', label: 'Back to monitor' }}
  />
);

const AdminNetworkWarnings = () => (
  <AdminNetworkPlaceholderView
    title="Network Warnings"
    subtitle="Review warning queues, triage operator action, and move into the relevant device onboarding flow."
    message="The warnings list UI is not available in this bundle yet. Review anomalies from the monitor overview until the dedicated warnings workflow is implemented."
    primaryLink={{ to: '/app/admin/network', label: 'Open monitor' }}
    secondaryLink={{ to: '/app/admin/network/security', label: 'Open security view' }}
  />
);

const AdminNetworkWarningDetail = () => {
  const { id } = useParams();

  return (
    <AdminNetworkPlaceholderView
      title={`Warning ${id || ''}`.trim()}
      subtitle="Dedicated warning-detail remediation is not implemented in this frontend yet."
      message="Open the network monitor to investigate the related anomaly or affected device, then return here once the warning-detail screen exists."
      primaryLink={{ to: '/app/admin/network', label: 'Open monitor' }}
      secondaryLink={{ to: '/app/admin/network/devices', label: 'Open devices' }}
    />
  );
};

const AdminNetworkCameras = () => (
  <AdminNetworkPlaceholderView
    title="Network Cameras"
    subtitle="Camera-specific monitoring will surface stream health, interface checks, and recorder relationships here."
    message="Camera-specific network workflows are not exposed in this UI yet. Use the device list and device detail pages for recorder and camera onboarding."
    primaryLink={{ to: '/app/admin/network/devices', label: 'Open devices' }}
    secondaryLink={{ to: '/app/admin/network', label: 'Back to monitor' }}
  />
);

const AdminNetworkSecurity = () => (
  <AdminNetworkPlaceholderView
    title="Network Security"
    subtitle="Security-focused monitoring will aggregate unauthorized accounts, integrity checks, and remediation status here."
    message="Security rollups are not implemented as a dedicated page yet. Use the anomaly detail and managed-interface account checks from device detail pages for now."
    primaryLink={{ to: '/app/admin/network', label: 'Open monitor' }}
    secondaryLink={{ to: '/app/admin/network/devices', label: 'Open devices' }}
  />
);

const AdminNetworkHealth = () => {
  const networkScope = useNetworkOwnerOrgScope();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(normalizeSummary({}));
  const [systems, setSystems] = useState([]);

  const loadHealth = async () => {
    if (networkScope.loading) {
      return;
    }

    try {
      setError('');
      setLoading(true);
      const [summaryResponse, truenasResponse, unifiResponse, pfsenseResponse, tuyaResponse, cameraResponse, haResponse] = await Promise.all([
        networkApi.getHealthSummary(networkScope.scopeParams),
        networkApi.getTruenasHealth(networkScope.scopeParams).catch(() => null),
        networkApi.getUnifiHealth(networkScope.scopeParams).catch(() => null),
        networkApi.getPfsenseHealth(networkScope.scopeParams).catch(() => null),
        networkApi.getTuyaHealth(networkScope.scopeParams).catch(() => null),
        networkApi.getCameraHealth(networkScope.scopeParams).catch(() => null),
        networkApi.getHaHealth(networkScope.scopeParams).catch(() => null),
      ]);

      const nextSummary = normalizeSummary(extractData(summaryResponse));
      const supplemental = [
        ['truenas', extractData(truenasResponse)],
        ['unifi', extractData(unifiResponse)],
        ['pfsense', extractData(pfsenseResponse)],
        ['tuya', extractData(tuyaResponse)],
        ['cameras', extractData(cameraResponse)],
        ['home_assistant', extractData(haResponse)],
      ]
        .filter(([, value]) => value)
        .map(([key, value]) => mapSystem(key, value));

      const merged = [...nextSummary.systems];
      supplemental.forEach((entry) => {
        if (!merged.some((item) => item.key === entry.key)) {
          merged.push(entry);
        }
      });

      setSummary(nextSummary);
      setSystems(merged);
    } catch (err) {
      console.error('Failed to load network health:', err);
      setError(getNetworkErrorMessage(err, 'Failed to load infrastructure health.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHealth();
  }, [networkScope.loading, networkScope.ownerOrgId, networkScope.selectedInstallationUuid]);

  useNetworkRefresh(() => {
    loadHealth();
  });

  return (
    <Layout>
      <div className="network-page">
        <SectionHeader title="Infrastructure Health" subtitle="Subsystem snapshots from TrueNAS, UniFi, Home Assistant, pfSense, Tuya, and cameras." />
        {loading ? <LoadingPanel title="Loading infrastructure health..." /> : null}
        {error ? <div className="network-error-banner">{error}</div> : null}
        {!loading ? (
          <>
            <div className="network-summary-grid">
              <SummaryCard label="Overall Status" value={String(summary.status || 'unknown').toUpperCase()} tone={getSeverityClass(summary.status)} />
              <SummaryCard label="Monitored Systems" value={systems.length} />
              <SummaryCard label="Open Anomalies" value={summary.unresolvedAnomalies} tone={summary.unresolvedAnomalies > 0 ? 'warning' : 'info'} />
            </div>
            <div className="network-system-grid">
              {systems.map((system) => (
                <section key={system.key} className={`network-system-card severity-${getSeverityClass(system.status)}`}>
                  <div className="network-panel-header">
                    <h2>{system.name}</h2>
                    <span className={`network-pill severity-${getSeverityClass(system.status)}`}>{system.status}</span>
                  </div>
                  <p>{system.detail || 'No detailed subsystem message returned.'}</p>
                  <div className="network-system-meta">
                    <span>Issues: {system.issueCount}</span>
                    <span>{system.updatedAt ? `Updated ${formatDateTime(system.updatedAt)}` : 'No timestamp provided'}</span>
                  </div>
                </section>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </Layout>
  );
};

const AdminNetworkScans = ({ embedded = false, networkScope = {} }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scans, setScans] = useState([]);
  const [scanTypeFilter, setScanTypeFilter] = useState('all');
  const { handleStepUpError, stepUpDialog } = useNetworkStepUp();

  const loadScans = async () => {
    try {
      setError('');
      setLoading(true);
      const response = await networkApi.getScans({
        ...networkScope,
        type: scanTypeFilter !== 'all' ? scanTypeFilter : undefined,
        limit: 50,
      });
      setScans(asArray(extractData(response), ['items', 'rows', 'scans']).map(normalizeScan));
    } catch (err) {
      console.error('Failed to load scans:', err);
      if (handleStepUpError(err, loadScans)) {
        setError('');
        return;
      }
      setError(getNetworkErrorMessage(err, 'Failed to load scan history.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScans();
  }, [scanTypeFilter, networkScope.ownerOrgId, networkScope.installationUuid]);

  useNetworkRefresh(() => {
    loadScans();
  });

  const content = (
      <div className="network-page">
        <SectionHeader
          title="Scan History"
          subtitle="Completed and running network monitoring jobs returned through the Mycelium network gateway."
          actions={<Link to="/app/admin/network" className="network-secondary-link">Back to monitor</Link>}
        />
        {stepUpDialog}
        <ManualScanControls onTriggered={loadScans} compact networkScope={networkScope} />
        <div className="network-toolbar">
          <select
            value={scanTypeFilter}
            onChange={(event) => setScanTypeFilter(event.target.value)}
            className="network-select"
            aria-label="Filter scan history by type"
          >
            {SCAN_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        {error ? <div className="network-error-banner">{error}</div> : null}
        {loading ? <LoadingPanel title="Loading scan history..." /> : null}
        {!loading && scans.length === 0 ? <EmptyState title="No scan history" text="The network gateway has not returned any scan log records yet." /> : null}
        {!loading && scans.length > 0 ? (
          <div className="network-table-wrap">
            <table className="network-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Devices</th>
                  <th>New</th>
                  <th>Anomalies</th>
                  <th>Duration</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {scans.map((scan) => (
                  <tr key={scan.id || `${scan.type}-${scan.startedAt}`}>
                    <td>{formatScanType(scan.type)}</td>
                    <td><span className={`network-pill severity-${getSeverityClass(scan.status)}`}>{scan.status}</span></td>
                    <td>{formatDateTime(scan.startedAt)}</td>
                    <td>{formatDateTime(scan.completedAt)}</td>
                    <td>{scan.devicesFound}</td>
                    <td>{scan.newDevices}</td>
                    <td>{scan.anomaliesFound}</td>
                    <td>{formatDuration(scan.durationMs)}</td>
                    <td>{scan.errorMessage || 'None'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
  );

  return embedded ? content : <Layout>{content}</Layout>;
};

const AdminNetworkSummaryWidget = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(normalizeSummary({}));

  const loadSummary = async () => {
    try {
      const response = await networkApi.getHealthSummary();
      setSummary(normalizeSummary(extractData(response)));
    } catch (err) {
      console.error('Failed to load dashboard network summary:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  useNetworkRefresh(() => {
    loadSummary();
  });

  return (
    <div className="widget widget-network-summary">
      <div className="widget-header">
        <h2>Network Health</h2>
        <Link to="/app/admin/network" className="link-view">Open monitor</Link>
      </div>
      {loading ? (
        <div className="network-widget-loading">Loading network monitor...</div>
      ) : (
        <>
          <div className="network-widget-status-row">
            <span className={`network-pill severity-${getSeverityClass(summary.status)}`}>{String(summary.status || 'unknown').toUpperCase()}</span>
            <span>{summary.unresolvedAnomalies} open anomalies</span>
          </div>
          <div className="network-widget-metrics">
            <div>
              <strong>{summary.deviceCount}</strong>
              <span>known devices</span>
            </div>
            <div>
              <strong>{summary.onlineDevices}</strong>
              <span>online now</span>
            </div>
            <div>
              <strong>{summary.systems.length}</strong>
              <span>subsystems</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export {
  AdminNetworkOverview,
  AdminNetworkTabbedPage,
  AdminNetworkDeviceListViewer,
  AdminNetworkDeviceListViewer as AdminNetworkDevices,
  AdminNetworkDeviceDetail,
  AdminNetworkDependencies,
  AdminNetworkWarnings,
  AdminNetworkWarningDetail,
  AdminNetworkCameras,
  AdminNetworkSecurity,
  AdminNetworkAnomalies,
  AdminNetworkAnomalyDetail,
  AdminNetworkHealth,
  AdminNetworkScans,
  AdminNetworkSummaryWidget,
};
